import {
  chunkW10DataChannelMessage,
  clientMessageSchema,
  createW10DataChannelLaneReceiver,
  decodeW10DataChannelFrame,
  parseW10ControlJsonBytes,
  PROTOCOL_CONSTANTS,
  decodeW10ExactControlRecord,
  decodeW10ReadyPayload,
  decodeW10RejectPayload,
  encodeW10ExactControlRecord,
  encodeW10VersionRangePayload,
  respondToW10VersionHello,
  serverMessageSchema,
  W10_DATA_CHANNEL_BACKPRESSURE_V1,
  W10_DATA_CHANNEL_KINDS,
  W10_DATA_CHANNEL_REASSEMBLY_V1,
  W10_DATA_CHANNEL_V1,
  type W10DataChannelLane,
  type W10DataChannelLaneReceiver,
  type W10ExactControlRecord,
  type W10PrimaryJoinAckSuccessPayload,
} from "@klass-share/protocol";
import {
  captureDisplay,
  classifyCaptureFailure,
  createSyntheticCanvasCapture,
  disposeCapture,
} from "./capture.js";
import type { HostDiscovery } from "./discovery.js";
import { WEB_DEPLOYMENT_CONFIG } from "./deployment-config.js";
import { createConnId, type IdentityStore } from "./identity.js";
import {
  applyRoomEvent,
  reconcileCaptureOwnership,
  roomEventFromWire,
} from "./room-state.js";
import {
  projectRetainedSignalingEvent,
  SignalingClient,
  type SignalingEvent,
  type SignalingClientOptions,
  type SignalingSnapshot,
} from "./signaling-client.js";
import { strings } from "./strings.js";
import { WebRtcManager } from "./webrtc.js";
import {
  assertW8JoinGrantUseWindow,
  decodeCanonicalBase64Url,
  verifyW8JoinGrantEnvelope,
  W8JoinGrantVerificationError,
  type W8JoinGrantVerifierClock,
  type W8PrevalidatedJoinGrant,
  type W8TransportVerificationKey,
} from "./w8-join-grant-verifier.js";
import type { Capabilities, StatsSnapshot } from "./wire.js";
import {
  WhiteboardSession,
  type W10ControlRecord,
  type W10LogicalLaneState,
  type W10LogicalMessage,
  type W10MemberCarrierIdentity,
  type W10MemberPrimaryCarrier,
  type W10PageCarrierFailure,
  type W10PageHardCause,
  type W10PageLocalCause,
  type W10PageOperationalCause,
  type W10PageSendResult,
  type WhiteboardConnectionOptions,
} from "./whiteboard/session.js";
import type { WhiteboardSessionSnapshot } from "@klass-share/whiteboard-core";

export interface W10MemberPeerSignal {
  readonly kind: "answer" | "ice";
  readonly data: string | null;
}

export interface W10EstablishedDtlsBinding {
  readonly kind: "webrtc-dtls-v1";
  readonly clientFingerprintSha256: string;
  readonly boardFingerprintSha256: string;
}

export interface W10MemberPeerOptions {
  readonly onSignal: (signal: W10MemberPeerSignal) => void;
  readonly onReady: (
    carrier: W10MemberPrimaryCarrier,
    binding: W10EstablishedDtlsBinding,
  ) => void;
  readonly onFailure: (failure: W10PageCarrierFailure) => void;
  readonly peerConnectionFactory?: () => RTCPeerConnection;
  readonly environment?: W10MemberPeerEnvironment;
}

export interface W10MemberPeerEnvironment {
  readonly crypto: Pick<Crypto, "subtle">;
  now(): number;
  setTimeout(task: () => void, delayMs: number): ReturnType<typeof setTimeout>;
  clearTimeout(handle: ReturnType<typeof setTimeout>): void;
}

export interface W10BootstrapRoute {
  readonly routeId: string;
  readonly licenseId: string;
  readonly hostId: string;
  readonly sessionId: string;
  readonly proto: 2;
}

export interface W10BootstrapJoinGrant extends W8PrevalidatedJoinGrant {
  readonly routeId: string;
}

export interface W10PrimaryAdmissionAttempt {
  receive(record: W10ControlRecord): void;
  beforeJoin(): Promise<boolean>;
  onJoinAck(): Promise<boolean>;
  /** Must return the exact enqueue result for session-confirmation-report. */
  afterMemberReadyAck(): Promise<W10PageSendResult>;
  close(): void;
}

/**
 * Injected W7 + signed-grant authority. W8 routing and W10 carrier/session
 * ordering stay in this module; the adapter can authorize join only after its
 * exact W7 and grant checks complete.
 */
export interface W10PrimaryAdmissionAdapter {
  /**
   * Compare the authenticated grant to the independently retained W6 anchor
   * before any peer connection is opened. This does not accept the W7 binding.
   */
  prevalidateGrant(input: {
    readonly route: W10BootstrapRoute;
    readonly grant: W10BootstrapJoinGrant;
    readonly grantNonce: string;
  }): boolean;
  start(input: {
    readonly route: W10BootstrapRoute;
    readonly grant: W10BootstrapJoinGrant;
    readonly grantNonce: string;
    readonly binding: W10EstablishedDtlsBinding;
    readonly send: (
      type: string,
      payload: Readonly<Record<string, unknown>>,
    ) => W10PageSendResult;
    readonly onAuthorized: () => void;
    readonly onHardFailure: () => void;
  }): W10PrimaryAdmissionAttempt;
}

export interface ClientControllerOptions {
  discovery: HostDiscovery;
  identityStore: IdentityStore;
  displayName: string;
  capabilities: Capabilities;
  appVersion?: string;
  captureDisplay?: () => Promise<MediaStream>;
  webSocketFactory?: SignalingClientOptions["webSocketFactory"];
  document?: SignalingClientOptions["document"];
  primaryAdmissionAdapter?: W10PrimaryAdmissionAdapter;
  bootstrapWebSocketFactory?: (url: string) => WebSocket;
  primaryPeerConnectionFactory?: () => RTCPeerConnection;
  rtcPeerConnectionFactory?: () => RTCPeerConnection;
  primaryPeerEnvironment?: W10MemberPeerEnvironment;
  /** Test-owned trust seam; shipped App omits it and uses the embedded bundle. */
  primaryTransportVerificationKeys?: readonly W8TransportVerificationKey[];
  /** Test-owned clock seam for frozen grant vectors. */
  primaryGrantClock?: () => W8JoinGrantVerifierClock;
  whiteboardFactory?: (
    options: WhiteboardConnectionOptions,
  ) => WhiteboardSession;
  /** Compile-gated E2E only: allows synthetic capture continuity across refresh. */
  e2eSessionStorage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
}

export interface ClientViewState {
  signaling: SignalingSnapshot;
  remoteStream?: MediaStream;
  remoteTrackMuted: boolean;
  localCaptureActive: boolean;
  captureRequestPending: boolean;
  needsCaptureGesture: boolean;
  captureMessage?: string;
  yjsReadyState?: number;
  whiteboard?: WhiteboardSessionSnapshot;
}

type Listener = (state: ClientViewState) => void;
type CaptureMode = "display" | "synthetic";

export class ClientController {
  readonly #discovery: HostDiscovery;
  readonly #identityStore: IdentityStore;
  readonly #listeners = new Set<Listener>();
  readonly #signaling: SignalingClient;
  readonly #primaryBootstrap: W10PrimaryBootstrapCoordinator | undefined;
  readonly #displayName: string;
  readonly #whiteboardFactory: NonNullable<
    ClientControllerOptions["whiteboardFactory"]
  >;
  #rtc: WebRtcManager;
  #whiteboard: WhiteboardSession | undefined;
  #unsubscribeWhiteboard: (() => void) | undefined;
  #state: ClientViewState;
  #captureStream: MediaStream | undefined;
  #captureMode: CaptureMode = "display";
  #pendingGrantId: string | undefined;
  #activeGrantId: string | undefined;
  #activationGrantId: string | undefined;
  #sessionId: string | undefined;
  #syntheticDeferred: Deferred<undefined> | undefined;
  readonly #e2eSessionStorage: ClientControllerOptions["e2eSessionStorage"];
  readonly #captureDisplay: () => Promise<MediaStream>;
  readonly #rtcPeerConnectionFactory: (() => RTCPeerConnection) | undefined;
  #acquiringGrantId: string | undefined;

  public constructor(options: ClientControllerOptions) {
    this.#discovery = options.discovery;
    this.#identityStore = options.identityStore;
    this.#displayName = options.displayName;
    this.#whiteboardFactory =
      options.whiteboardFactory ??
      ((connection) => new WhiteboardSession(connection));
    this.#e2eSessionStorage = options.e2eSessionStorage;
    this.#captureDisplay = options.captureDisplay ?? captureDisplay;
    this.#rtcPeerConnectionFactory = options.rtcPeerConnectionFactory;
    this.#signaling = new SignalingClient({
      discovery: options.discovery,
      identityStore: options.identityStore,
      displayName: options.displayName,
      capabilities: options.capabilities,
      ...(options.appVersion === undefined
        ? {}
        : { appVersion: options.appVersion }),
      ...(options.webSocketFactory === undefined
        ? {}
        : { webSocketFactory: options.webSocketFactory }),
      ...(options.document === undefined ? {} : { document: options.document }),
    });
    this.#state = {
      signaling: this.#signaling.snapshot,
      remoteTrackMuted: false,
      localCaptureActive: false,
      captureRequestPending: false,
      needsCaptureGesture: false,
    };
    this.#rtc = this.#createRtc(this.#state.signaling.clientId);
    this.#primaryBootstrap =
      options.primaryAdmissionAdapter === undefined
        ? undefined
        : new W10PrimaryBootstrapCoordinator({
            discovery: options.discovery,
            identityStore: options.identityStore,
            displayName: options.displayName,
            capabilities: options.capabilities,
            ...(options.appVersion === undefined
              ? {}
              : { appVersion: options.appVersion }),
            admissionAdapter: options.primaryAdmissionAdapter,
            ...(options.bootstrapWebSocketFactory === undefined
              ? {}
              : { webSocketFactory: options.bootstrapWebSocketFactory }),
            ...(options.primaryPeerConnectionFactory === undefined
              ? {}
              : {
                  peerConnectionFactory: options.primaryPeerConnectionFactory,
                }),
            ...(options.primaryPeerEnvironment === undefined
              ? {}
              : { peerEnvironment: options.primaryPeerEnvironment }),
            ...(options.primaryTransportVerificationKeys === undefined
              ? {}
              : {
                  transportVerificationKeys:
                    options.primaryTransportVerificationKeys,
                }),
            ...(options.primaryGrantClock === undefined
              ? {}
              : { grantClock: options.primaryGrantClock }),
            ...(options.document === undefined
              ? {}
              : { document: options.document }),
            onJoinAccepted: (payload) => this.#acceptPrimaryJoin(payload),
            onAdmitted: (identity, carrier) =>
              this.installPrimaryWhiteboardCarrier(identity, carrier),
            onControlRecord: (record, connId) =>
              this.#handlePrimaryControlRecord(record, connId),
            onFailure: (failure) => this.#handlePrimaryCarrierFailure(failure),
            onAvailabilityFailure: () => this.#signaling.connect(),
            onBootstrapTerminal: (code) => this.#handleBootstrapTerminal(code),
          });
    this.#signaling.subscribe((event) => this.#handleSignalEvent(event));
    this.#restoreSyntheticCapture();
  }

  public get state(): ClientViewState {
    return this.#state;
  }

  public subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener(this.#state);
    return () => this.#listeners.delete(listener);
  }

  public connect(): void {
    if (this.#whiteboard?.memberCarrier === "pc-primary") return;
    if (this.#primaryBootstrap !== undefined) {
      this.#primaryBootstrap.connect();
      return;
    }
    this.#signaling.connect();
  }

  public disconnect(sendLeave = false): void {
    const primaryLeaveAttempted =
      this.#primaryBootstrap?.close(sendLeave) ?? false;
    this.#signaling.stop(sendLeave && !primaryLeaveAttempted);
    this.#rtc.closeAll();
    this.#disposeWhiteboard(false);
    this.#stopCaptureTracks();
  }

  public get whiteboardSession(): WhiteboardSession | undefined {
    return this.#whiteboard;
  }

  /**
   * Handoff from the verified W8/W7/member-ready coordinator. The coordinator
   * may call this only after the successful join-ack identity is durable and
   * the exact member-ready-ack was accepted.
   */
  public installPrimaryWhiteboardCarrier(
    identity: W10MemberCarrierIdentity,
    carrier: W10MemberPrimaryCarrier,
  ): boolean {
    if (
      this.#state.signaling.phase === "room-closed" ||
      this.#state.signaling.phase === "terminal-error" ||
      this.#state.signaling.phase === "stopped" ||
      !Number.isSafeInteger(identity.memberEpoch) ||
      identity.memberEpoch <= 0 ||
      identity.sessionId.length === 0 ||
      identity.memberLegId.length === 0 ||
      this.#whiteboard !== undefined
    ) {
      carrier.close();
      return false;
    }
    if (
      this.#sessionId !== undefined &&
      this.#sessionId !== identity.sessionId
    ) {
      this.#rtc.resetForSession();
      this.#stopCaptureTracks();
    }
    this.#sessionId = identity.sessionId;
    this.#whiteboard = this.#whiteboardFactory({
      discovery: this.#discovery,
      clientId: this.#state.signaling.clientId,
      sessionId: identity.sessionId,
      primaryCarrier: carrier,
      memberLegId: identity.memberLegId,
      memberEpoch: identity.memberEpoch,
      displayName: this.#displayName,
      onPrimaryRecoverableFailure: () => {
        // D-10 uses the durable resume credential on a new direct /signal
        // socket. Its new join-ack supplies the fresh /yjs ticket below.
        this.#signaling.connect();
      },
      onPrimaryProtocolFailure: () => {
        // The session installs its unavailable/no-fallback fence first.
      },
    });
    this.#unsubscribeWhiteboard = this.#whiteboard.subscribe(() =>
      this.#emit(),
    );
    this.#emit();
    return true;
  }

  #acceptPrimaryJoin(payload: W10PrimaryJoinAckSuccessPayload): void {
    this.#identityStore.saveResume(this.#discovery.hostId, {
      resumeToken: payload.resumeToken,
      sessionId: payload.sessionId,
    });
    this.#state = {
      ...this.#state,
      signaling: {
        ...this.#state.signaling,
        phase: "joined",
        room: applyRoomEvent(undefined, {
          type: "snapshot",
          roomState: payload.roomState,
        }),
        resumed: payload.resumed,
        consecutiveFailureMs: 0,
      },
    };
    this.#emit();
  }

  #handlePrimaryCarrierFailure(failure: W10PageCarrierFailure): void {
    const whiteboard = this.#whiteboard;
    const hadPrimarySession =
      whiteboard?.memberCarrier === "pc-primary" ||
      whiteboard?.memberCarrier === "fallback-pending";
    if (whiteboard !== undefined && hadPrimarySession) {
      whiteboard.retirePrimaryCarrier(failure);
    }
    if (failure.class === "policy") {
      this.#state = {
        ...this.#state,
        signaling: {
          ...this.#state.signaling,
          phase: "terminal-error",
          lastError: {
            code: failure.cause,
            message: "Primary carrier rate-limit policy failure",
            fatal: false,
          },
        },
      };
      this.#emit();
      return;
    }
    if (hadPrimarySession && failure.class !== "hard") return;
    if (failure.class !== "hard") {
      this.#signaling.connect();
      return;
    }
    this.#signaling.stop(false);
    this.#state = {
      ...this.#state,
      signaling: {
        ...this.#state.signaling,
        phase: "terminal-error",
        lastError: {
          code: failure.cause,
          message: "Primary carrier integrity failure",
          fatal: true,
        },
      },
    };
    this.#emit();
  }

  #handlePrimaryControlRecord(
    record: W10ControlRecord,
    primaryConnId: string | undefined,
  ): void {
    const payload = record.payload as Record<string, unknown>;
    const roomEvent = roomEventFromWire(record.type, payload);
    if (roomEvent !== undefined) {
      const room = applyRoomEvent(this.#state.signaling.room, roomEvent);
      this.#handleSignalEvent({
        type: "state",
        snapshot: { ...this.#state.signaling, room },
      });
      return;
    }
    const event = projectRetainedSignalingEvent(record.type, payload);
    if (event !== undefined) {
      this.#handleSignalEvent(event);
      return;
    }
    if (
      record.type === "transport-changed" &&
      (payload.mode === "direct" ||
        payload.mode === "relay" ||
        payload.mode === "unreachable")
    ) {
      this.#handleSignalEvent({
        type: "state",
        snapshot: { ...this.#state.signaling, transportMode: payload.mode },
      });
      return;
    }
    if (record.type === "error") {
      const code = typeof payload.code === "string" ? payload.code : "INTERNAL";
      const message =
        typeof payload.message === "string"
          ? payload.message
          : "Protocol error";
      const fatal = payload.fatal === true;
      const phase = fatal
        ? code === "ROOM_CLOSED"
          ? "room-closed"
          : "terminal-error"
        : this.#state.signaling.phase;
      this.#handleSignalEvent({
        type: "state",
        snapshot: {
          ...this.#state.signaling,
          phase,
          lastError: { code, message, fatal },
        },
      });
      this.#handleSignalEvent({
        type: "protocol-error",
        code,
        message,
        fatal,
      });
      return;
    }
    if (record.type !== "kick") return;
    if (
      payload.reason === "superseded" &&
      payload.supersededBy === primaryConnId
    ) {
      return;
    }
    const phase =
      payload.reason === "superseded"
        ? "manual-reconnect"
        : payload.reason === "room-closed"
          ? "room-closed"
          : "stopped";
    this.#handleSignalEvent({
      type: "state",
      snapshot: {
        ...this.#state.signaling,
        phase,
        ...(phase === "room-closed"
          ? {
              lastError: {
                code: "ROOM_CLOSED",
                message: strings.errors.signaling.roomClosed,
                fatal: true,
              },
            }
          : {}),
      },
    });
  }

  #handleBootstrapTerminal(code: string): void {
    this.#state = {
      ...this.#state,
      signaling: {
        ...this.#state.signaling,
        phase: "terminal-error",
        lastError: {
          code,
          message: "Primary bootstrap failed closed",
          fatal: true,
        },
      },
    };
    this.#emit();
  }

  public retryAfterLnaRecovery(): void {
    this.#signaling.retryAfterLnaRecovery();
  }

  public prepareFreshJoinAfterSupersede(): boolean {
    if (this.#state.signaling.phase !== "manual-reconnect") return false;
    return this.#signaling.prepareFreshJoinAfterExternalPrimarySupersede();
  }

  /** Called directly by the user gesture; capture waits for the host grant. */
  public requestDisplayShare(): void {
    if (
      this.#state.localCaptureActive ||
      this.#state.captureRequestPending ||
      this.#acquiringGrantId !== undefined
    )
      return;
    this.#captureMode = "display";
    const requested = this.#sendControlPlane("share-request", {});
    this.#state = {
      ...this.#state,
      captureMessage: undefined,
      captureRequestPending: requested,
      needsCaptureGesture: false,
    };
    this.#emit();
  }

  /** Available only through the compile-time-gated acceptance API. */
  public startSyntheticShare(): Promise<undefined> {
    if (this.#syntheticDeferred !== undefined)
      return this.#syntheticDeferred.promise;
    if (
      this.#state.localCaptureActive ||
      this.#state.captureRequestPending ||
      this.#acquiringGrantId !== undefined
    )
      return Promise.reject(new Error("capture request is already pending"));
    this.#captureMode = "synthetic";
    const pendingCapture = deferred<undefined>();
    this.#syntheticDeferred = pendingCapture;
    const requested = this.#sendControlPlane("share-request", {});
    this.#state = { ...this.#state, captureRequestPending: requested };
    this.#emit();
    if (!requested) {
      this.#syntheticDeferred = undefined;
      pendingCapture.reject(new Error("signaling socket is not joined"));
    }
    return pendingCapture.promise;
  }

  /** Fresh user activation after an InvalidStateError or moderated approval. */
  public startGrantedCapture(): void {
    if (this.#pendingGrantId !== undefined)
      void this.#acquireAndReady(this.#pendingGrantId);
  }

  public stopShare(): void {
    this.#sendControlPlane("share-stop", { reason: "cancelled" });
    this.#clearSyntheticPersistence();
    this.#stopCaptureTracks();
  }

  public failDirectLeg(): string | undefined {
    return this.#rtc.failDirectLeg();
  }

  public getLegs(): ReturnType<WebRtcManager["getLegs"]> {
    return this.#rtc.getLegs();
  }

  public collectStats(): Promise<StatsSnapshot[]> {
    return this.#rtc.collectStats();
  }

  public waitForJoined(timeoutMs = 10_000): Promise<void> {
    if (this.#state.signaling.phase === "joined") return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        unsubscribe();
        reject(new Error("timed out waiting for join"));
      }, timeoutMs);
      const unsubscribe = this.subscribe((state) => {
        if (state.signaling.phase !== "joined") return;
        clearTimeout(timeout);
        unsubscribe();
        resolve();
      });
    });
  }

  #createRtc(clientId: string): WebRtcManager {
    return new WebRtcManager({
      clientId,
      send: (type, payload) => this.#sendControlPlane(type, payload),
      ...(this.#rtcPeerConnectionFactory === undefined
        ? {}
        : { peerConnectionFactory: this.#rtcPeerConnectionFactory }),
      onRemoteStream: (stream) => {
        this.#state = {
          ...this.#state,
          ...(stream === undefined
            ? { remoteStream: undefined }
            : { remoteStream: stream }),
        };
        this.#emit();
      },
      onTrackMuted: (remoteTrackMuted) => {
        this.#state = { ...this.#state, remoteTrackMuted };
        this.#emit();
      },
    });
  }

  #sendControlPlane(
    type: string,
    payload: Readonly<Record<string, unknown>>,
  ): boolean {
    const primaryResult = this.#primaryBootstrap?.sendControl(type, payload);
    return primaryResult === undefined
      ? this.#signaling.send(type, payload)
      : primaryResult.status === "accepted";
  }

  #handleSignalEvent(event: SignalingEvent): void {
    if (event.type === "state") {
      const previousClientId = this.#state.signaling.clientId;
      this.#state = { ...this.#state, signaling: event.snapshot };
      if (event.snapshot.clientId !== previousClientId) {
        this.#rtc.closeAll();
        this.#rtc = this.#createRtc(event.snapshot.clientId);
      }
      if (
        event.snapshot.phase === "reconnecting" ||
        event.snapshot.phase === "reconnect-escalated"
      ) {
        if (
          !shouldPreserveRtcDuringReconnect(
            event.snapshot,
            this.#state.localCaptureActive,
          )
        ) {
          this.#rtc.closeAll();
        }
      }
      if (
        event.snapshot.room?.share.status === "idle" &&
        this.#state.remoteStream !== undefined
      ) {
        this.#rtc.closeAll();
      }
      if (shouldTearDownClientResources(event.snapshot)) {
        this.#primaryBootstrap?.close();
        this.#rtc.closeAll();
        this.#disposeWhiteboard(event.snapshot.phase === "room-closed");
        this.#clearSyntheticPersistence();
        this.#stopCaptureTracks();
      }
      this.#reconcileCapture();
      this.#emit();
      return;
    }
    if (event.type === "leg-create") {
      void this.#rtc.handleLegCreate(event.payload).catch(() => {
        this.#sendControlPlane("leg-status", {
          legId: event.payload.legId,
          state: "failed",
        });
      });
      return;
    }
    if (event.type === "leg-close") {
      this.#rtc.handleLegClose(event.legId);
      return;
    }
    if (event.type === "rtc-signal") {
      void this.#rtc.handleSignal(event.payload).catch(() => {
        this.#sendControlPlane("leg-status", {
          legId: event.payload.legId,
          state: "failed",
        });
      });
      return;
    }
    if (event.type === "share-decision") {
      this.#handleShareDecision(event.payload);
      return;
    }
    if (event.type === "share-revoke") {
      this.#state = { ...this.#state, captureMessage: event.reason };
      this.#clearSyntheticPersistence();
      this.#stopCaptureTracks();
      this.#emit();
      return;
    }
    if (event.type === "protocol-error" && event.code === "STALE_SHARE") {
      this.#clearSyntheticPersistence();
      this.#stopCaptureTracks();
      return;
    }
    if (event.type === "joined")
      this.#handleJoined(event.payload.sessionId, event.payload.resumed);
  }

  #handleShareDecision(payload: Record<string, unknown>): void {
    if (payload.granted !== true || typeof payload.grantId !== "string") {
      const reason =
        typeof payload.reason === "string" ? payload.reason : "denied";
      this.#state = {
        ...this.#state,
        captureMessage: shareDenialMessage(reason),
      };
      this.#clearSyntheticPersistence();
      this.#stopCaptureTracks();
      this.#syntheticDeferred?.reject(new Error(reason));
      this.#syntheticDeferred = undefined;
      this.#emit();
      return;
    }
    const grantId = payload.grantId;
    if (this.#activeGrantId === grantId && this.#captureStream !== undefined) {
      this.#sendControlPlane("share-ready", { grantId });
      return;
    }
    if (
      this.#pendingGrantId !== undefined ||
      this.#acquiringGrantId !== undefined
    )
      return;
    this.#pendingGrantId = grantId;
    this.#state = { ...this.#state, captureRequestPending: true };
    this.#emit();
    void this.#acquireAndReady(grantId);
  }

  async #acquireAndReady(grantId: string): Promise<void> {
    if (
      this.#pendingGrantId !== grantId ||
      this.#acquiringGrantId !== undefined
    )
      return;
    this.#acquiringGrantId = grantId;
    try {
      const stream =
        this.#captureMode === "synthetic"
          ? createSyntheticCanvasCapture()
          : await this.#captureDisplay();
      if (this.#pendingGrantId !== grantId) {
        stopTracks(stream);
        return;
      }
      this.#setCaptureStream(stream);
      this.#activeGrantId = grantId;
      this.#activationGrantId = grantId;
      this.#pendingGrantId = undefined;
      this.#sendControlPlane("share-ready", { grantId });
      if (this.#captureMode === "synthetic")
        this.#persistSyntheticCapture(grantId);
      this.#syntheticDeferred?.resolve(undefined);
      this.#syntheticDeferred = undefined;
    } catch (error) {
      const failure = classifyCaptureFailure(error);
      this.#state = {
        ...this.#state,
        needsCaptureGesture: failure.kind === "needs-gesture",
        captureMessage: failure.message,
      };
      if (failure.kind !== "needs-gesture") {
        if (failure.kind === "failed") {
          const capabilities = this.#signaling.markScreenCaptureUnavailable(
            false,
            false,
          );
          if (capabilities !== undefined) {
            const room = this.#state.signaling.room;
            this.#state = {
              ...this.#state,
              signaling: {
                ...this.#state.signaling,
                capabilities,
                ...(room === undefined
                  ? {}
                  : {
                      room: {
                        ...room,
                        peers: room.peers.map((peer) =>
                          peer.clientId === this.#state.signaling.clientId
                            ? {
                                ...peer,
                                capabilities: { ...capabilities },
                              }
                            : peer,
                        ),
                      },
                    }),
              },
            };
            this.#emit();
            this.#sendControlPlane("peer-update", { capabilities });
          }
        }
        this.#clearSyntheticPersistence();
        this.#pendingGrantId = undefined;
        this.#sendControlPlane("share-stop", {
          reason: failure.kind === "cancelled" ? "cancelled" : "capture-failed",
        });
      }
      this.#syntheticDeferred?.reject(error);
      this.#syntheticDeferred = undefined;
      this.#emit();
    } finally {
      if (this.#acquiringGrantId === grantId)
        this.#acquiringGrantId = undefined;
      const captureRequestPending = this.#pendingGrantId !== undefined;
      if (this.#state.captureRequestPending !== captureRequestPending) {
        this.#state = { ...this.#state, captureRequestPending };
        this.#emit();
      }
    }
  }

  #setCaptureStream(stream: MediaStream): void {
    this.#stopCaptureTracks();
    this.#captureStream = stream;
    this.#rtc.setLocalStream(stream);
    this.#state = {
      ...this.#state,
      localCaptureActive: true,
      captureRequestPending: false,
      needsCaptureGesture: false,
      captureMessage: undefined,
    };
    for (const track of stream.getTracks()) {
      track.addEventListener("ended", () => {
        if (this.#captureStream !== stream) return;
        if (
          stream
            .getVideoTracks()
            .some((videoTrack) => videoTrack.readyState === "live")
        )
          return;
        this.#sendControlPlane("share-stop", { reason: "cancelled" });
        this.#stopCaptureTracks();
      });
    }
    this.#emit();
  }

  #stopCaptureTracks(): void {
    if (this.#captureStream !== undefined) stopTracks(this.#captureStream);
    this.#captureStream = undefined;
    this.#rtc.setLocalStream(undefined);
    this.#activeGrantId = undefined;
    this.#pendingGrantId = undefined;
    this.#activationGrantId = undefined;
    this.#state = {
      ...this.#state,
      localCaptureActive: false,
      captureRequestPending: this.#acquiringGrantId !== undefined,
      needsCaptureGesture: false,
    };
    this.#emit();
  }

  #handleJoined(sessionId: string, resumed: boolean): void {
    this.#primaryBootstrap?.close();
    if (this.#sessionId !== sessionId) {
      this.#rtc.resetForSession();
      if (this.#sessionId !== undefined) this.#stopCaptureTracks();
    }
    this.#sessionId = sessionId;
    const yjs = this.#state.signaling.yjs;
    if (yjs !== undefined) {
      if (this.#whiteboard?.sessionId === yjs.sessionId) {
        this.#whiteboard.reconnect(yjs.ticket);
      } else {
        this.#disposeWhiteboard(this.#whiteboard !== undefined);
        this.#whiteboard = this.#whiteboardFactory({
          discovery: this.#discovery,
          clientId: this.#state.signaling.clientId,
          sessionId: yjs.sessionId,
          yjsTicket: yjs.ticket,
          displayName: this.#displayName,
        });
        this.#unsubscribeWhiteboard = this.#whiteboard.subscribe(() =>
          this.#emit(),
        );
      }
    }
    if (resumed) {
      const share = this.#state.signaling.room?.share;
      if (
        share?.status === "active" &&
        share.sourceClientId === this.#state.signaling.clientId
      ) {
        if (
          this.#captureStream !== undefined &&
          this.#activeGrantId !== undefined
        ) {
          this.#sendControlPlane("share-ready", {
            grantId: this.#activeGrantId,
          });
        } else {
          this.#sendControlPlane("share-stop", {
            reason: "capture-failed",
          });
        }
      }
    }
    this.#emit();
  }

  #reconcileCapture(): void {
    const room = this.#state.signaling.room;
    if (room === undefined || this.#captureStream === undefined) return;
    const reconciliation = reconcileCaptureOwnership(
      room,
      this.#state.signaling.clientId,
      this.#pendingGrantId !== undefined,
      this.#activationGrantId !== undefined,
    );
    if (!reconciliation.activationInFlight) this.#activationGrantId = undefined;
    if (reconciliation.releaseCapture) {
      this.#clearSyntheticPersistence();
      this.#stopCaptureTracks();
    }
  }

  #emit(): void {
    this.#state = {
      ...this.#state,
      ...(this.#whiteboard === undefined
        ? { yjsReadyState: undefined, whiteboard: undefined }
        : {
            yjsReadyState: this.#whiteboard.snapshot.readyState,
            whiteboard: this.#whiteboard.snapshot,
          }),
    };
    for (const listener of this.#listeners) listener(this.#state);
  }

  #persistSyntheticCapture(grantId: string): void {
    this.#e2eSessionStorage?.setItem(
      syntheticKey(this.#discovery.hostId),
      JSON.stringify({ grantId }),
    );
  }

  #restoreSyntheticCapture(): void {
    const storage = this.#e2eSessionStorage;
    if (storage === undefined) return;
    const raw = storage.getItem(syntheticKey(this.#discovery.hostId));
    if (raw === null) return;
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "grantId" in parsed &&
        typeof parsed.grantId === "string"
      ) {
        this.#captureMode = "synthetic";
        this.#activeGrantId = restoreGrantAfterCaptureCleanup(
          parsed.grantId,
          () =>
            this.#setCaptureStream(
              createSyntheticCanvasCapture(
                "KlassShare E2E resume · 안녕하세요",
              ),
            ),
        );
      }
    } catch {
      storage.removeItem(syntheticKey(this.#discovery.hostId));
    }
  }

  #clearSyntheticPersistence(): void {
    this.#e2eSessionStorage?.removeItem(syntheticKey(this.#discovery.hostId));
  }

  #disposeWhiteboard(clearPersistence: boolean): void {
    this.#unsubscribeWhiteboard?.();
    this.#unsubscribeWhiteboard = undefined;
    const whiteboard = this.#whiteboard;
    this.#whiteboard = undefined;
    if (whiteboard !== undefined)
      void whiteboard.destroy(clearPersistence).catch(() => undefined);
  }
}

interface W10PrimaryBootstrapCoordinatorOptions {
  readonly discovery: HostDiscovery;
  readonly identityStore: IdentityStore;
  readonly displayName: string;
  readonly capabilities: Capabilities;
  readonly appVersion?: string;
  readonly admissionAdapter: W10PrimaryAdmissionAdapter;
  readonly webSocketFactory?: (url: string) => WebSocket;
  readonly peerConnectionFactory?: () => RTCPeerConnection;
  readonly peerEnvironment?: W10MemberPeerEnvironment;
  readonly transportVerificationKeys?: readonly W8TransportVerificationKey[];
  readonly grantClock?: () => W8JoinGrantVerifierClock;
  readonly document?: Pick<
    Document,
    "hidden" | "addEventListener" | "removeEventListener"
  >;
  readonly onJoinAccepted: (payload: W10PrimaryJoinAckSuccessPayload) => void;
  readonly onAdmitted: (
    identity: W10MemberCarrierIdentity,
    carrier: W10MemberPrimaryCarrier,
  ) => boolean;
  readonly onControlRecord: (
    record: W10ControlRecord,
    connId: string | undefined,
  ) => void;
  readonly onFailure: (failure: W10PageCarrierFailure) => void;
  readonly onAvailabilityFailure: () => void;
  readonly onBootstrapTerminal: (code: string) => void;
}

type W10PrimaryBootstrapPhase =
  | "idle"
  | "routing"
  | "admission"
  | "awaiting-join-cut"
  | "awaiting-join-ack"
  | "awaiting-membership-cut"
  | "awaiting-member-ready-send"
  | "awaiting-member-ready-ack"
  | "awaiting-confirmation-cut"
  | "admitted"
  | "terminal";

interface W8BootstrapRecord {
  readonly type: string;
  readonly seq: number;
  readonly payload: Readonly<Record<string, unknown>>;
}

const W8_BOOTSTRAP_TIMEOUT_MS = 10_000;
const W10_MEMBER_READY_TIMEOUT_MS = 8_000;
const W8_SOFT_AVAILABILITY_CODES = new Set([
  "BOOTSTRAP_UNAVAILABLE",
  "BOOTSTRAP_TIMEOUT",
  "BOARD_UNAVAILABLE",
  "GRANT_UNAVAILABLE",
  "INTERNAL",
]);
const W8_BOOTSTRAP_CODES = new Set([
  "BOOTSTRAP_UNAVAILABLE",
  "BOOTSTRAP_TIMEOUT",
  "BOARD_UNAVAILABLE",
  "ORIGIN_REJECTED",
  "UNSUPPORTED_BOOTSTRAP_SCHEMA",
  "UNSUPPORTED_GRANT_SCHEMA",
  "VERSION_MISMATCH",
  "INVALID_PAYLOAD",
  "UNSUPPORTED_TYPE",
  "BOARD_AUTH_FAILED",
  "AUTHORITY_REVOKED",
  "GRANT_UNAVAILABLE",
  "GRANT_DENIED",
  "GRANT_INVALID",
  "GRANT_EXPIRED",
  "GRANT_REQUIRED",
  "SEAT_CAP_EXCEEDED",
  "SESSION_MISMATCH",
  "SESSION_STATEMENT_INVALID",
  "RATE_LIMITED",
  "INTERNAL",
]);
const W8_ROUTE_CLOSE_REASONS = new Set([
  "complete",
  "peer-closed",
  "board-replaced",
  "session-ended",
  "timeout",
  "error",
]);

class UnsupportedW8BootstrapSchemaError extends Error {}

/** W8 browser route + W10 pre-admission/member-ready production coordinator. */
class W10PrimaryBootstrapCoordinator {
  readonly #options: W10PrimaryBootstrapCoordinatorOptions;
  readonly #webSocketFactory: (url: string) => WebSocket;
  #phase: W10PrimaryBootstrapPhase = "idle";
  #socket: WebSocket | undefined;
  #peer: W10MemberPeer | undefined;
  #carrier: W10MemberPrimaryCarrier | undefined;
  #binding: W10EstablishedDtlsBinding | undefined;
  #route: W10BootstrapRoute | undefined;
  #grant: W10BootstrapJoinGrant | undefined;
  #grantNonce: string | undefined;
  #memberIdentity: W10MemberCarrierIdentity | undefined;
  #memberConnId: string | undefined;
  #admission: W10PrimaryAdmissionAttempt | undefined;
  #removeRecordListener: (() => void) | undefined;
  #removeControlWritableListener: (() => void) | undefined;
  #pendingJoinPayload: Readonly<Record<string, unknown>> | undefined;
  #pendingMemberReadyPayload: Readonly<Record<string, unknown>> | undefined;
  #outboundSequence = 0;
  #inboundSequence = 0;
  #routeTimer: ReturnType<typeof setTimeout> | undefined;
  #preAdmissionRequestTimer: ReturnType<typeof setTimeout> | undefined;
  #preAdmissionJoinTimer: ReturnType<typeof setTimeout> | undefined;
  #memberReadyTimer: ReturnType<typeof setTimeout> | undefined;
  #livenessTimer: ReturnType<typeof setTimeout> | undefined;
  #pingTimer: ReturnType<typeof setTimeout> | undefined;
  #heartbeatIntervalMs: number = PROTOCOL_CONSTANTS.T_HB_INTERVAL;
  #heartbeatTimeoutMs: number = PROTOCOL_CONSTANTS.T_HB_TIMEOUT;
  #syncDeferred = false;
  #pingDeferred = false;
  #visibilityListenerRegistered = false;
  #softCarrierFailurePending = false;
  #grantConsumed = false;

  constructor(options: W10PrimaryBootstrapCoordinatorOptions) {
    this.#options = options;
    this.#webSocketFactory =
      options.webSocketFactory ?? ((url) => new WebSocket(url));
  }

  connect(): void {
    if (this.#phase !== "idle") return;
    this.#phase = "routing";
    this.#grantNonce = randomBase64Url16();
    const endpoint = new URL(
      "/v1/signaling/client",
      WEB_DEPLOYMENT_CONFIG.shareOrigin,
    );
    endpoint.protocol = "wss:";
    const socket = this.#webSocketFactory(endpoint.href);
    this.#socket = socket;
    this.#routeTimer = setTimeout(
      () => this.#failAvailability(),
      W8_BOOTSTRAP_TIMEOUT_MS,
    );
    socket.addEventListener("open", () => {
      if (this.#socket !== socket || this.#phase !== "routing") return;
      this.#sendBootstrap("client-bootstrap-open", {
        schemaVersion: 1,
        intendedHostId: this.#options.discovery.hostId,
        grantNonce: this.#grantNonce,
        proto: { min: 2, max: 2 },
      });
    });
    socket.addEventListener("message", (event) => {
      if (this.#socket !== socket) return;
      if (typeof event.data !== "string") {
        this.#failTerminal("INVALID_PAYLOAD");
        return;
      }
      this.#receiveBootstrap(event.data);
    });
    socket.addEventListener("close", () => {
      if (this.#socket !== socket) return;
      this.#socket = undefined;
      if (
        this.#phase !== "admitted" &&
        this.#phase !== "terminal" &&
        this.#carrier === undefined
      ) {
        this.#failAvailability();
      }
    });
  }

  sendControl(
    type: string,
    payload: Readonly<Record<string, unknown>>,
  ): W10PageSendResult | undefined {
    if (this.#phase !== "admitted" || this.#carrier === undefined)
      return undefined;
    return this.#carrier.control.send(type, payload);
  }

  close(sendLeave = false): boolean {
    const admittedCarrier =
      this.#phase === "admitted" ? this.#carrier : undefined;
    const leaveAttempted = sendLeave && admittedCarrier !== undefined;
    this.#phase = "terminal";
    this.#softCarrierFailurePending = false;
    this.#clearTimers();
    this.#removeRecordListener?.();
    this.#removeRecordListener = undefined;
    this.#removeControlWritableListener?.();
    this.#removeControlWritableListener = undefined;
    this.#admission?.close();
    this.#admission = undefined;
    try {
      if (leaveAttempted) admittedCarrier.control.send("leave", {});
    } catch {
      // Explicit teardown consumes this single best-effort attempt.
    } finally {
      this.#peer?.close();
      this.#peer = undefined;
      this.#socket?.close(1000, "primary bootstrap stopped");
      this.#socket = undefined;
    }
    return leaveAttempted;
  }

  #receiveBootstrap(source: string): void {
    let record: W8BootstrapRecord;
    try {
      record = decodeW8BootstrapRecord(source);
    } catch (error) {
      this.#failTerminal(
        error instanceof UnsupportedW8BootstrapSchemaError
          ? "UNSUPPORTED_BOOTSTRAP_SCHEMA"
          : "INVALID_PAYLOAD",
      );
      return;
    }
    if (record.seq !== this.#inboundSequence + 1) {
      this.#failTerminal("INVALID_PAYLOAD");
      return;
    }
    this.#inboundSequence = record.seq;
    try {
      switch (record.type) {
        case "bootstrap-route":
          this.#acceptRoute(record.payload);
          return;
        case "join-grant":
          this.#acceptGrant(record.payload);
          return;
        case "bootstrap-rtc-signal":
          this.#acceptRtcSignal(record.payload);
          return;
        case "bootstrap-error":
          this.#acceptBootstrapError(record.payload);
          return;
        case "bootstrap-route-close":
          this.#acceptRouteClose(record.payload);
          return;
        default:
          this.#failTerminal("UNSUPPORTED_TYPE");
      }
    } catch (error) {
      this.#failTerminal(
        error instanceof W8JoinGrantVerificationError
          ? error.code
          : "INVALID_PAYLOAD",
      );
    }
  }

  #acceptRoute(payload: Readonly<Record<string, unknown>>): void {
    requireOrderedKeys(payload, [
      "schemaVersion",
      "routeId",
      "licenseId",
      "hostId",
      "sessionId",
      "proto",
    ]);
    if (this.#route !== undefined || this.#phase !== "routing")
      throw new TypeError();
    const proto = payload.proto;
    if (
      payload.schemaVersion !== 1 ||
      !isBase64Url16(payload.routeId) ||
      !isUuidV4String(payload.licenseId) ||
      payload.hostId !== this.#options.discovery.hostId ||
      !isUuidV4String(payload.sessionId) ||
      proto !== 2
    ) {
      throw new TypeError();
    }
    this.#route = Object.freeze({
      routeId: payload.routeId,
      licenseId: payload.licenseId,
      hostId: payload.hostId,
      sessionId: payload.sessionId,
      proto: 2,
    });
    this.#maybeStartAdmission();
  }

  #acceptGrant(payload: Readonly<Record<string, unknown>>): void {
    requireOrderedKeys(payload, ["schemaVersion", "routeId", "envelope"]);
    const route = this.#route;
    if (
      payload.schemaVersion !== 1 ||
      this.#grant !== undefined ||
      route === undefined ||
      payload.routeId !== route.routeId ||
      !isPlainObject(payload.envelope)
    ) {
      throw new TypeError();
    }
    const verified = verifyW8JoinGrantEnvelope(
      payload.envelope,
      {
        licenseId: route.licenseId,
        hostId: route.hostId,
        sessionId: route.sessionId,
        protocolVersion: route.proto,
        grantNonce: this.#grantNonce ?? "",
      },
      this.#grantClock(),
      this.#options.transportVerificationKeys,
    );
    const grant: W10BootstrapJoinGrant = Object.freeze({
      routeId: route.routeId,
      envelope: verified.envelope,
      payload: verified.payload,
      receivedMonotonicMilliseconds: verified.receivedMonotonicMilliseconds,
    });
    if (
      this.#grantNonce === undefined ||
      !this.#options.admissionAdapter.prevalidateGrant({
        route,
        grant,
        grantNonce: this.#grantNonce,
      })
    ) {
      throw new W8JoinGrantVerificationError("GRANT_INVALID");
    }
    this.#grant = grant;
    this.#maybeStartAdmission();
  }

  #acceptRtcSignal(payload: Readonly<Record<string, unknown>>): void {
    requireOrderedKeys(payload, ["schemaVersion", "routeId", "kind", "data"]);
    const route = this.#route;
    if (
      payload.schemaVersion !== 1 ||
      route === undefined ||
      payload.routeId !== route.routeId ||
      (payload.kind !== "offer" && payload.kind !== "ice")
    ) {
      throw new TypeError();
    }
    if (payload.kind === "offer") {
      if (typeof payload.data !== "string" || payload.data.length === 0)
        throw new TypeError();
      if (this.#peer !== undefined || this.#grant === undefined)
        throw new TypeError();
      this.#createPeer(payload.data);
      return;
    }
    if (
      payload.data !== null &&
      (typeof payload.data !== "string" || payload.data.length === 0)
    ) {
      throw new TypeError();
    }
    if (this.#peer === undefined) throw new TypeError();
    void this.#peer.acceptIce(payload.data);
  }

  #createPeer(offer: string): void {
    const peer = new W10MemberPeer({
      ...(this.#options.peerConnectionFactory === undefined
        ? {}
        : { peerConnectionFactory: this.#options.peerConnectionFactory }),
      ...(this.#options.peerEnvironment === undefined
        ? {}
        : { environment: this.#options.peerEnvironment }),
      onSignal: (signal) => {
        const route = this.#route;
        if (route === undefined) return;
        this.#sendBootstrap("bootstrap-rtc-signal", {
          schemaVersion: 1,
          routeId: route.routeId,
          kind: signal.kind,
          data: signal.data,
        });
      },
      onReady: (carrier, binding) => {
        if (this.#peer !== peer || this.#phase !== "routing") {
          carrier.close();
          return;
        }
        this.#carrier = carrier;
        this.#binding = binding;
        this.#phase = "admission";
        this.#clearRouteTimer();
        this.#armPreAdmissionRequestTimer();
        this.#removeRecordListener = carrier.control.onRecord(
          this.#receiveControlRecord,
        );
        this.#removeControlWritableListener = carrier.control.onWritable(
          this.#onControlWritable,
        );
        this.#maybeStartAdmission();
      },
      onFailure: (failure) => {
        if (this.#peer !== peer) return;
        if (this.#phase === "terminal") {
          if (this.#softCarrierFailurePending && failure.class === "hard") {
            this.#softCarrierFailurePending = false;
            this.#options.onFailure(failure);
          }
          return;
        }
        const wasAdmitted = this.#phase === "admitted";
        this.#softCarrierFailurePending = failure.class !== "hard";
        this.#phase = "terminal";
        this.#clearTimers();
        this.#removeRecordListener?.();
        this.#removeRecordListener = undefined;
        this.#removeControlWritableListener?.();
        this.#removeControlWritableListener = undefined;
        this.#admission?.close();
        this.#admission = undefined;
        if (failure.class !== "hard" && !wasAdmitted) {
          peer.retireForFallback();
        }
        this.#options.onFailure(failure);
        if (failure.class !== "hard" && wasAdmitted) {
          peer.retireForFallback();
        }
      },
    });
    this.#peer = peer;
    void peer.acceptOffer(offer);
  }

  #maybeStartAdmission(): void {
    if (
      this.#phase !== "admission" ||
      this.#admission !== undefined ||
      this.#route === undefined ||
      this.#grant === undefined ||
      this.#grantNonce === undefined ||
      this.#binding === undefined ||
      this.#carrier === undefined
    ) {
      return;
    }
    this.#admission = this.#options.admissionAdapter.start({
      route: this.#route,
      grant: this.#grant,
      grantNonce: this.#grantNonce,
      binding: this.#binding,
      send: (type, payload) => {
        const result = this.#carrier?.control.send(type, payload) ?? {
          status: "paused" as const,
        };
        if (
          type === "session-statement-request" &&
          result.status === "accepted"
        ) {
          this.#clearPreAdmissionRequestTimer();
        }
        return result;
      },
      onAuthorized: () => this.#consumeGrantAndSendJoin(),
      onHardFailure: () => this.#peer?.abortHard("PRE_ADMISSION_CONTROL"),
    });
  }

  #grantClock(): W8JoinGrantVerifierClock {
    return (
      this.#options.grantClock?.() ?? {
        wallClockSeconds: Math.floor(Date.now() / 1_000),
        monotonicMilliseconds: performance.now(),
      }
    );
  }

  #consumeGrantAndSendJoin(): void {
    if (this.#phase !== "admission" || this.#grant === undefined) return;
    if (this.#grantConsumed) {
      this.#peer?.abortHard("PRE_ADMISSION_CONTROL");
      return;
    }
    try {
      assertW8JoinGrantUseWindow(this.#grant, this.#grantClock());
    } catch (error) {
      this.#failTerminal(
        error instanceof W8JoinGrantVerificationError
          ? error.code
          : "GRANT_INVALID",
      );
      return;
    }
    this.#grantConsumed = true;
    this.#sendJoin();
  }

  readonly #receiveControlRecord = (record: W10ControlRecord): void => {
    const membershipCommitted =
      this.#phase === "awaiting-member-ready-send" ||
      this.#phase === "awaiting-member-ready-ack" ||
      this.#phase === "awaiting-confirmation-cut" ||
      this.#phase === "admitted";
    if (record.type === "member-ready") {
      this.#peer?.abortHard(
        membershipCommitted
          ? "MEMBER_READY_INTEGRITY"
          : "PRE_ADMISSION_CONTROL",
      );
      return;
    }
    if (record.type === "join-ack" && this.#phase !== "awaiting-join-ack") {
      this.#peer?.abortHard("PRE_ADMISSION_CONTROL");
      return;
    }
    if (
      record.type === "member-ready-ack" &&
      this.#phase !== "awaiting-member-ready-ack"
    ) {
      this.#peer?.abortHard(
        membershipCommitted
          ? "MEMBER_READY_INTEGRITY"
          : "PRE_ADMISSION_CONTROL",
      );
      return;
    }
    if (record.type === "error" && record.payload.code === "RATE_LIMITED") {
      return;
    }
    if (this.#phase === "admitted") {
      this.#armLivenessTimer();
      if (record.type === "pong") {
        this.#schedulePingFromPong();
      } else if (record.type === "session-confirmation-result") {
        this.#admission?.receive(record);
      }
      this.#options.onControlRecord(record, this.#memberConnId);
      return;
    }
    if (this.#phase === "admission") {
      this.#admission?.receive(record);
      return;
    }
    if (this.#phase === "awaiting-join-ack") {
      if (record.type !== "join-ack") {
        this.#peer?.abortHard("PRE_ADMISSION_CONTROL");
        return;
      }
      void this.#acceptJoinAck(record.payload);
      return;
    }
    if (this.#phase === "awaiting-member-ready-ack") {
      if (record.type !== "member-ready-ack") {
        this.#peer?.abortHard("MEMBER_READY_INTEGRITY");
        return;
      }
      void this.#acceptMemberReadyAck(record.payload);
      return;
    }
    this.#peer?.abortHard("PRE_ADMISSION_CONTROL");
  };

  #sendJoin(): void {
    if (this.#phase !== "admission" || this.#carrier === undefined) return;
    this.#phase = "awaiting-join-cut";
    this.#armPreAdmissionJoinTimer();
    void this.#sendJoinAfterCut();
  }

  async #sendJoinAfterCut(): Promise<void> {
    const admission = this.#admission;
    if (admission === undefined) return;
    let allowed: boolean;
    try {
      allowed = await admission.beforeJoin();
    } catch {
      allowed = false;
    }
    if (this.#phase !== "awaiting-join-cut") return;
    if (!allowed) {
      this.#peer?.abortHard("PRE_ADMISSION_CONTROL");
      return;
    }
    const carrier = this.#carrier;
    if (carrier === undefined) return;
    const identity = this.#options.identityStore.load(
      this.#options.discovery.hostId,
    );
    // P2-18 remains OPEN: no non-soft continuation commit is enabled here.
    const connId = createConnId();
    this.#memberConnId = connId;
    this.#pendingJoinPayload = Object.freeze({
      clientId: identity.clientId,
      connId,
      displayName: this.#options.displayName,
      capabilities: this.#options.capabilities,
      resume: false,
      proto: {
        min: PROTOCOL_CONSTANTS.PROTO_VERSION_MIN,
        max: PROTOCOL_CONSTANTS.PROTO_VERSION_MAX,
      },
      ...(this.#options.appVersion === undefined
        ? {}
        : { appVersion: this.#options.appVersion }),
    });
    this.#submitPendingJoin();
  }

  #submitPendingJoin(): void {
    if (
      this.#phase !== "awaiting-join-cut" ||
      this.#pendingJoinPayload === undefined
    ) {
      return;
    }
    const result = this.#carrier?.control.send(
      "join",
      this.#pendingJoinPayload,
    );
    if (result?.status === "paused") return;
    if (result?.status !== "accepted") {
      this.#peer?.abortOperational("W7_RESPONSE_TIMEOUT");
      return;
    }
    this.#pendingJoinPayload = undefined;
    this.#clearPreAdmissionJoinTimer();
    this.#phase = "awaiting-join-ack";
  }

  async #acceptJoinAck(
    payload: Readonly<Record<string, unknown>>,
  ): Promise<void> {
    if (payload.ok !== true) {
      const code =
        typeof payload.code === "string" ? payload.code : "INVALID_PAYLOAD";
      this.#failTerminal(code);
      return;
    }
    const route = this.#route;
    if (route === undefined) throw new TypeError();
    const success = payload as unknown as W10PrimaryJoinAckSuccessPayload;
    if (
      success.sessionId !== route.sessionId ||
      success.proto !== PROTOCOL_CONSTANTS.PROTO_VERSION_MAX
    ) {
      this.#peer?.abortHard("PRE_ADMISSION_CONTROL");
      return;
    }
    this.#heartbeatIntervalMs = success.heartbeat.intervalMs;
    this.#heartbeatTimeoutMs = success.heartbeat.timeoutMs;
    this.#phase = "awaiting-membership-cut";
    let memberCommitted: boolean;
    try {
      memberCommitted = (await this.#admission?.onJoinAck()) === true;
    } catch {
      memberCommitted = false;
    }
    if (this.#phase !== "awaiting-membership-cut") return;
    if (!memberCommitted) {
      this.#carrier?.control.send("leave", {});
      this.#peer?.abortHard("PRE_ADMISSION_CONTROL");
      return;
    }
    this.#options.onJoinAccepted(success);
    const identity: W10MemberCarrierIdentity = Object.freeze({
      sessionId: success.sessionId,
      memberLegId: success.memberLegId,
      memberEpoch: success.memberEpoch,
    });
    this.#memberIdentity = identity;
    this.#pendingMemberReadyPayload = Object.freeze({
      sessionId: identity.sessionId,
      memberLegId: identity.memberLegId,
      memberEpoch: identity.memberEpoch,
    });
    this.#phase = "awaiting-member-ready-send";
    this.#memberReadyTimer = setTimeout(
      () => this.#peer?.abortOperational("MEMBER_READY_TIMEOUT"),
      W10_MEMBER_READY_TIMEOUT_MS,
    );
    this.#submitPendingMemberReady();
  }

  #submitPendingMemberReady(): void {
    if (
      this.#phase !== "awaiting-member-ready-send" ||
      this.#pendingMemberReadyPayload === undefined
    ) {
      return;
    }
    const result = this.#carrier?.control.send(
      "member-ready",
      this.#pendingMemberReadyPayload,
    );
    if (result?.status === "paused") return;
    if (result?.status !== "accepted") {
      this.#peer?.abortOperational("MEMBER_READY_TIMEOUT");
      return;
    }
    this.#pendingMemberReadyPayload = undefined;
    this.#phase = "awaiting-member-ready-ack";
  }

  async #acceptMemberReadyAck(
    payload: Readonly<Record<string, unknown>>,
  ): Promise<void> {
    const route = this.#route;
    const identity = this.#memberIdentity;
    if (
      route === undefined ||
      identity === undefined ||
      payload.sessionId !== identity.sessionId ||
      payload.memberLegId !== identity.memberLegId ||
      payload.memberEpoch !== identity.memberEpoch
    ) {
      this.#peer?.abortHard("MEMBER_READY_INTEGRITY");
      return;
    }
    this.#clearMemberReadyTimer();
    this.#peer?.markMemberReady();
    this.#phase = "awaiting-confirmation-cut";
    let confirmationReport: W10PageSendResult;
    try {
      confirmationReport =
        (await this.#admission?.afterMemberReadyAck()) ?? PAUSED_SEND_RESULT;
    } catch {
      confirmationReport = PAUSED_SEND_RESULT;
    }
    if (this.#phase !== "awaiting-confirmation-cut") return;
    if (confirmationReport.status !== "accepted") {
      this.#peer?.abortOperational("W7_RESPONSE_TIMEOUT");
      return;
    }
    const carrier = this.#carrier;
    if (carrier === undefined || !this.#options.onAdmitted(identity, carrier)) {
      this.#peer?.abortHard("MEMBER_READY_INTEGRITY");
      return;
    }
    this.#phase = "admitted";
    this.#startHeartbeat();
    this.#peer?.markAdmitted();
    this.#sendBootstrap("bootstrap-route-close", {
      schemaVersion: 1,
      routeId: route.routeId,
      reason: "complete",
    });
    this.#socket?.close(1000, "bootstrap complete");
  }

  #acceptBootstrapError(payload: Readonly<Record<string, unknown>>): void {
    requireOrderedKeys(payload, [
      "schemaVersion",
      "code",
      "refSeq",
      "retryAfterSeconds",
    ]);
    if (
      payload.schemaVersion !== 1 ||
      typeof payload.code !== "string" ||
      !W8_BOOTSTRAP_CODES.has(payload.code) ||
      !(
        payload.refSeq === null ||
        (Number.isSafeInteger(payload.refSeq) && (payload.refSeq as number) > 0)
      ) ||
      !(
        payload.retryAfterSeconds === null ||
        (Number.isSafeInteger(payload.retryAfterSeconds) &&
          (payload.retryAfterSeconds as number) >= 0)
      )
    ) {
      throw new TypeError();
    }
    if (W8_SOFT_AVAILABILITY_CODES.has(payload.code)) {
      this.#failAvailability();
    } else {
      this.#failTerminal(payload.code);
    }
  }

  #acceptRouteClose(payload: Readonly<Record<string, unknown>>): void {
    requireOrderedKeys(payload, ["schemaVersion", "routeId", "reason"]);
    if (
      payload.schemaVersion !== 1 ||
      payload.routeId !== this.#route?.routeId ||
      typeof payload.reason !== "string" ||
      !W8_ROUTE_CLOSE_REASONS.has(payload.reason)
    ) {
      throw new TypeError();
    }
    if (this.#carrier === undefined) this.#failAvailability();
  }

  #sendBootstrap(
    type: string,
    payload: Readonly<Record<string, unknown>>,
  ): boolean {
    const socket = this.#socket;
    if (socket?.readyState !== 1) return false;
    this.#outboundSequence += 1;
    socket.send(
      JSON.stringify({
        schemaVersion: 1,
        type,
        seq: this.#outboundSequence,
        payload,
      }),
    );
    return true;
  }

  #startHeartbeat(): void {
    this.#ensureVisibilityListener();
    this.#requestSync();
    this.#sendPing();
    this.#armLivenessTimer();
  }

  #sendPing(): void {
    if (this.#phase !== "admitted") return;
    const result = this.#carrier?.control.send("ping", {});
    this.#pingDeferred = result?.status === "paused";
  }

  #requestSync(): void {
    if (this.#phase !== "admitted") return;
    const result = this.#carrier?.control.send("sync-request", {});
    this.#syncDeferred = result?.status === "paused";
  }

  readonly #onControlWritable = (): void => {
    if (this.#phase === "awaiting-join-cut") {
      this.#submitPendingJoin();
      return;
    }
    if (this.#phase === "awaiting-member-ready-send") {
      this.#submitPendingMemberReady();
      return;
    }
    if (this.#phase === "admitted") {
      if (this.#syncDeferred) this.#requestSync();
      if (this.#pingDeferred) this.#sendPing();
    }
  };

  #schedulePingFromPong(): void {
    if (this.#pingTimer !== undefined) clearTimeout(this.#pingTimer);
    this.#pingTimer = setTimeout(() => {
      this.#pingTimer = undefined;
      this.#sendPing();
    }, this.#heartbeatIntervalMs);
  }

  #armLivenessTimer(): void {
    if (this.#livenessTimer !== undefined) clearTimeout(this.#livenessTimer);
    const timeout = this.#options.document?.hidden
      ? Math.max(
          this.#heartbeatTimeoutMs,
          PROTOCOL_CONSTANTS.T_HIDDEN_PONG_TOLERANCE,
        )
      : this.#heartbeatTimeoutMs;
    this.#livenessTimer = setTimeout(() => {
      this.#livenessTimer = undefined;
      this.#peer?.abortOperational("PRIMARY_HOST_LIVENESS_TIMEOUT");
    }, timeout);
  }

  readonly #onVisibilityChange = (): void => {
    if (this.#phase !== "admitted") return;
    this.#armLivenessTimer();
    if (this.#options.document?.hidden === false) {
      if (this.#pingTimer !== undefined) clearTimeout(this.#pingTimer);
      this.#pingTimer = undefined;
      this.#sendPing();
      this.#requestSync();
    }
  };

  #ensureVisibilityListener(): void {
    if (
      this.#options.document === undefined ||
      this.#visibilityListenerRegistered
    ) {
      return;
    }
    this.#options.document.addEventListener(
      "visibilitychange",
      this.#onVisibilityChange,
    );
    this.#visibilityListenerRegistered = true;
  }

  #removeVisibilityListener(): void {
    if (
      this.#options.document === undefined ||
      !this.#visibilityListenerRegistered
    ) {
      return;
    }
    this.#options.document.removeEventListener(
      "visibilitychange",
      this.#onVisibilityChange,
    );
    this.#visibilityListenerRegistered = false;
  }

  #failAvailability(): void {
    if (this.#phase === "terminal" || this.#phase === "admitted") return;
    this.#phase = "terminal";
    this.#clearTimers();
    this.#admission?.close();
    this.#peer?.close();
    this.#socket?.close(1000, "bootstrap unavailable");
    this.#options.onAvailabilityFailure();
  }

  #failTerminal(code: string): void {
    if (this.#phase === "terminal" || this.#phase === "admitted") return;
    this.#phase = "terminal";
    this.#clearTimers();
    this.#admission?.close();
    this.#peer?.close();
    this.#socket?.close(1000, "bootstrap failed closed");
    this.#options.onBootstrapTerminal(code);
  }

  #clearTimers(): void {
    this.#clearRouteTimer();
    this.#clearPreAdmissionRequestTimer();
    this.#clearPreAdmissionJoinTimer();
    this.#clearMemberReadyTimer();
    if (this.#livenessTimer !== undefined) clearTimeout(this.#livenessTimer);
    if (this.#pingTimer !== undefined) clearTimeout(this.#pingTimer);
    this.#livenessTimer = undefined;
    this.#pingTimer = undefined;
    this.#syncDeferred = false;
    this.#pingDeferred = false;
    this.#removeVisibilityListener();
  }

  #clearRouteTimer(): void {
    if (this.#routeTimer !== undefined) clearTimeout(this.#routeTimer);
    this.#routeTimer = undefined;
  }

  #armPreAdmissionRequestTimer(): void {
    this.#clearPreAdmissionRequestTimer();
    this.#preAdmissionRequestTimer = setTimeout(() => {
      this.#preAdmissionRequestTimer = undefined;
      this.#peer?.abortOperational("PRE_ADMISSION_IDLE_TIMEOUT");
    }, PROTOCOL_CONSTANTS.T_CONNECT);
  }

  #clearPreAdmissionRequestTimer(): void {
    if (this.#preAdmissionRequestTimer !== undefined) {
      clearTimeout(this.#preAdmissionRequestTimer);
      this.#preAdmissionRequestTimer = undefined;
    }
  }

  #armPreAdmissionJoinTimer(): void {
    this.#clearPreAdmissionJoinTimer();
    this.#preAdmissionJoinTimer = setTimeout(() => {
      this.#preAdmissionJoinTimer = undefined;
      this.#peer?.abortOperational("PRE_ADMISSION_IDLE_TIMEOUT");
    }, PROTOCOL_CONSTANTS.T_CONNECT);
  }

  #clearPreAdmissionJoinTimer(): void {
    if (this.#preAdmissionJoinTimer !== undefined) {
      clearTimeout(this.#preAdmissionJoinTimer);
      this.#preAdmissionJoinTimer = undefined;
    }
  }

  #clearMemberReadyTimer(): void {
    if (this.#memberReadyTimer !== undefined)
      clearTimeout(this.#memberReadyTimer);
    this.#memberReadyTimer = undefined;
  }
}

function decodeW8BootstrapRecord(source: string): W8BootstrapRecord {
  const bytes = new TextEncoder().encode(source);
  if (bytes.byteLength > PROTOCOL_CONSTANTS.MAX_MSG_SIZE) throw new TypeError();
  const parsed = parseW10ControlJsonBytes(bytes);
  if (!isPlainObject(parsed)) throw new TypeError();
  requireOrderedKeys(parsed, ["schemaVersion", "type", "seq", "payload"]);
  const hasWellFormedHeader =
    Number.isSafeInteger(parsed.schemaVersion) &&
    (parsed.schemaVersion as number) > 0 &&
    typeof parsed.type === "string" &&
    /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/u.test(parsed.type) &&
    Number.isSafeInteger(parsed.seq) &&
    (parsed.seq as number) > 0 &&
    isPlainObject(parsed.payload);
  if (hasWellFormedHeader && (parsed.schemaVersion as number) > 1) {
    throw new UnsupportedW8BootstrapSchemaError();
  }
  if (!hasWellFormedHeader || parsed.schemaVersion !== 1) {
    throw new TypeError();
  }
  return Object.freeze({
    type: parsed.type as string,
    seq: parsed.seq as number,
    payload: Object.freeze({
      ...(parsed.payload as Readonly<Record<string, unknown>>),
    }),
  });
}

function requireOrderedKeys(
  value: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): void {
  const actual = Object.keys(value);
  if (
    actual.length !== keys.length ||
    keys.some((key, index) => actual[index] !== key)
  ) {
    throw new TypeError();
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isBase64Url16(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    decodeCanonicalBase64Url(value, 16, "W8 routeId");
    return true;
  } catch {
    return false;
  }
}

function isUuidV4String(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(
      value,
    )
  );
}

function randomBase64Url16(): string {
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return globalThis
    .btoa(binary)
    .replace(/\+/gu, "-")
    .replace(/\//gu, "_")
    .replace(/=+$/u, "");
}
interface W10OutboundJob {
  readonly messageId: number;
  readonly frames: readonly Uint8Array[];
  readonly byteLength: number;
  nextFrame: number;
}

interface W10ControlIntent {
  readonly type: string;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly estimatedByteLength: number;
  readonly exemptionKey?: string;
}

interface W10InboundPartial {
  readonly messageId: number;
  readonly wireVersion: number;
  readonly kind: number;
  readonly chunkCount: number;
  readonly totalPayloadByteLength: number;
  readonly frames: Uint8Array[];
  idleTimer?: ReturnType<typeof setTimeout>;
  absoluteTimer?: ReturnType<typeof setTimeout>;
}

interface W10MemberLane {
  readonly lane: W10DataChannelLane;
  readonly receiver: W10DataChannelLaneReceiver;
  readonly messages: Set<(message: W10LogicalMessage) => void>;
  readonly states: Set<(state: W10LogicalLaneState) => void>;
  readonly writable: Set<() => void>;
  readonly outbound: W10OutboundJob[];
  channel?: RTCDataChannel;
  partial?: W10InboundPartial;
  nextOutboundMessageId: number;
  lastOutboundMessageId: number;
  pendingByteLength: number;
  lastInboundMessageId: number;
  paused: boolean;
  drainTimer?: ReturnType<typeof setTimeout>;
  sendProgressTimer?: ReturnType<typeof setTimeout>;
}

const DEFAULT_W10_MEMBER_ENVIRONMENT: W10MemberPeerEnvironment = {
  get crypto() {
    return globalThis.crypto;
  },
  now: () => globalThis.performance.now(),
  setTimeout: (task, delayMs) => setTimeout(task, delayMs),
  clearTimeout: (handle) => clearTimeout(handle),
};

const UINT32_MAX = 0xffff_ffff;

/**
 * Page half of one W10 member PC. The board remains the sole offerer and DCEP
 * channel creator; this class never calls addTrack, addTransceiver, createOffer,
 * or restartIce.
 */
export class W10MemberPeer {
  readonly #options: W10MemberPeerOptions;
  readonly #environment: W10MemberPeerEnvironment;
  readonly #connection: RTCPeerConnection;
  readonly #recordListeners = new Set<(record: W10ControlRecord) => void>();
  readonly #lanes: Readonly<Record<W10DataChannelLane, W10MemberLane>>;
  readonly #carrier: W10MemberPrimaryCarrier;
  #remoteDescriptionSet = false;
  #offerAccepted = false;
  #closed = false;
  #applicationReady = false;
  #memberReady = false;
  #helloSent = false;
  #peerHelloSeen = false;
  #readySentVersion: number | undefined;
  #localReadyFlushed = false;
  #peerReadySeen = false;
  #nextControlSequence = 1;
  #lastControlSequence = 0;
  #hasObservedControlRecord = false;
  #queuedCandidates: Array<RTCIceCandidateInit | null> = [];
  #dtlsTransport: RTCDtlsTransport | undefined;
  #openTimer: ReturnType<typeof setTimeout> | undefined;
  #iceDisconnectTimer: ReturnType<typeof setTimeout> | undefined;
  #pumping = false;
  #whiteboardYieldPending = false;
  #whiteboardWritableNotificationPending = false;
  #controlWritableNotificationTimer: ReturnType<typeof setTimeout> | undefined;
  #controlWritableNotificationAt = Number.POSITIVE_INFINITY;
  #controlIntentPendingByteLength = 0;
  #controlIntents: W10ControlIntent[] = [];
  #rateRecoveryReconstruction: W10ControlIntent[] = [];
  #lastStrictControlIntent: W10ControlIntent | undefined;
  #latestDirtyControlIntent: W10ControlIntent | undefined;
  #controlTokens = 0;
  #controlLastRefill = 0;
  #controlSyncNotBefore = 0;
  #rateRecoveryPending = false;
  #rateRecoveryStrikeObserved = false;
  #rateRecoveryReleaseAt = 0;
  #rateRecoveryDrainThroughMessageId = 0;
  #rateRecoveryTimer: ReturnType<typeof setTimeout> | undefined;
  readonly #usedRateExemptions = new Set<string>();
  #terminalSendResult: W10PageSendResult | undefined;
  #dtlsObservationStarted = false;
  #dtlsObservationPending = false;
  #pendingSoftFailure:
    | Extract<
        W10PageCarrierFailure,
        { readonly class: "operational" | "local" }
      >
    | undefined;
  #softFailurePending = false;
  #retiredForFallback = false;
  #admitted = false;
  #heldPostReadyControlRecord: W10ControlRecord | undefined;

  public constructor(options: W10MemberPeerOptions) {
    this.#options = options;
    this.#environment = options.environment ?? DEFAULT_W10_MEMBER_ENVIRONMENT;
    this.#connection =
      options.peerConnectionFactory?.() ??
      new RTCPeerConnection({ iceServers: [] });
    this.#lanes = {
      control: this.#createLane("control"),
      whiteboard: this.#createLane("whiteboard"),
    };
    const logicalState = () => this.#logicalState();
    const lastWhiteboardMessageId = () =>
      this.#lanes.whiteboard.lastInboundMessageId;
    const lastOutboundWhiteboardMessageId = () =>
      this.#lanes.whiteboard.lastOutboundMessageId;
    const whiteboardWritable = () => this.#isWhiteboardWritable();
    const controlWritable = () => this.#isControlWritable();
    this.#carrier = {
      control: {
        get state() {
          return logicalState();
        },
        get writable() {
          return controlWritable();
        },
        send: (type, payload) => this.#sendControlRecord(type, payload),
        onRecord: (listener) => {
          this.#recordListeners.add(listener);
          return () => this.#recordListeners.delete(listener);
        },
        onState: (listener) => this.#listenState(this.#lanes.control, listener),
        onWritable: (listener) => {
          this.#lanes.control.writable.add(listener);
          return () => this.#lanes.control.writable.delete(listener);
        },
      },
      whiteboard: {
        get state() {
          return logicalState();
        },
        get writable() {
          return whiteboardWritable();
        },
        get lastInboundMessageId() {
          return lastWhiteboardMessageId();
        },
        get lastOutboundMessageId() {
          return lastOutboundWhiteboardMessageId();
        },
        send: (payload) => this.#sendWhiteboard(payload),
        onMessage: (listener) => {
          this.#lanes.whiteboard.messages.add(listener);
          return () => this.#lanes.whiteboard.messages.delete(listener);
        },
        onState: (listener) =>
          this.#listenState(this.#lanes.whiteboard, listener),
        onWritable: (listener) => {
          this.#lanes.whiteboard.writable.add(listener);
          return () => this.#lanes.whiteboard.writable.delete(listener);
        },
      },
      retireForFallback: () => this.retireForFallback(),
      close: () => this.close(),
    };

    this.#connection.addEventListener("datachannel", this.#onDataChannel);
    this.#connection.addEventListener("icecandidate", this.#onIceCandidate);
    this.#connection.addEventListener("track", this.#onTrack);
    this.#connection.addEventListener(
      "connectionstatechange",
      this.#onConnectionState,
    );
    this.#connection.addEventListener(
      "iceconnectionstatechange",
      this.#onIceConnectionState,
    );
  }

  public get connection(): RTCPeerConnection {
    return this.#connection;
  }

  public async acceptOffer(sdp: string): Promise<void> {
    if (this.#closed || this.#softFailurePending) return;
    if (this.#offerAccepted || !isDataOnlySdp(sdp)) {
      this.#failHard("CHANNEL_CONTRACT_MISMATCH");
      return;
    }
    this.#offerAccepted = true;
    this.#armOpenDeadline();
    try {
      await this.#connection.setRemoteDescription({ type: "offer", sdp });
    } catch {
      this.#failHard("CHANNEL_CONTRACT_MISMATCH");
      return;
    }
    try {
      if (this.#closed) return;
      this.#remoteDescriptionSet = true;
      const negotiatedMaximum = (
        this.#connection.sctp as
          | (RTCSctpTransport & { readonly maxMessageSize?: unknown })
          | null
      )?.maxMessageSize;
      if (
        negotiatedMaximum !== undefined &&
        (typeof negotiatedMaximum !== "number" ||
          !(negotiatedMaximum >= W10_DATA_CHANNEL_V1.maxSendByteLength))
      ) {
        this.#failOperational(null);
        return;
      }
      if (!this.#installDtlsObserver()) return;
      await this.#flushMemberCandidates();
      if (this.#closed) return;
      const answer = await this.#connection.createAnswer();
      if (!isDataOnlySdp(answer.sdp)) {
        this.#failHard("CHANNEL_CONTRACT_MISMATCH");
        return;
      }
      await this.#connection.setLocalDescription(answer);
      if (this.#closed) return;
      const local = this.#connection.localDescription ?? answer;
      if (typeof local.sdp !== "string" || !isDataOnlySdp(local.sdp)) {
        this.#failHard("CHANNEL_CONTRACT_MISMATCH");
        return;
      }
      this.#options.onSignal({ kind: "answer", data: local.sdp });
    } catch {
      this.#failOperational("DTLS_OBSERVATION_UNAVAILABLE");
    }
  }

  /** Called only after the exact inbound member-ready-ack has been accepted. */
  public markMemberReady(): void {
    if (!this.#closed && !this.#softFailurePending && this.#applicationReady) {
      this.#memberReady = true;
      this.#onIceConnectionState();
    }
  }

  /** Opens application delivery only after the page confirmation cut installed its consumers. */
  public markAdmitted(): void {
    if (
      this.#closed ||
      this.#softFailurePending ||
      !this.#applicationReady ||
      !this.#memberReady
    ) {
      return;
    }
    this.#admitted = true;
    const held = this.#heldPostReadyControlRecord;
    this.#heldPostReadyControlRecord = undefined;
    if (held !== undefined) {
      for (const listener of this.#recordListeners) listener(held);
    }
    if (this.#lanes.whiteboard.writable.size > 0) {
      this.#notifyWhiteboardWritable();
    }
  }

  public abortOperational(cause: W10PageOperationalCause): void {
    this.#failOperational(cause);
  }

  public abortHard(cause: W10PageHardCause): void {
    this.#failHard(cause);
  }

  public async acceptIce(candidate: string | null): Promise<void> {
    if (this.#closed || this.#softFailurePending) return;
    const normalized = candidate === null ? null : { candidate };
    if (!this.#remoteDescriptionSet) {
      this.#queuedCandidates.push(normalized);
      return;
    }
    try {
      await this.#connection.addIceCandidate(normalized);
    } catch {
      this.#failHard("CHANNEL_CONTRACT_MISMATCH");
    }
  }

  public close(): void {
    if (this.#closed) return;
    this.#closed = true;
    this.#clearOpenDeadline();
    this.#clearControlWritableTimer();
    if (this.#rateRecoveryTimer !== undefined) {
      this.#environment.clearTimeout(this.#rateRecoveryTimer);
      this.#rateRecoveryTimer = undefined;
    }
    for (const lane of Object.values(this.#lanes)) this.#closeLane(lane);
    this.#recordListeners.clear();
    this.#connection.removeEventListener("datachannel", this.#onDataChannel);
    this.#connection.removeEventListener("icecandidate", this.#onIceCandidate);
    this.#connection.removeEventListener("track", this.#onTrack);
    this.#connection.removeEventListener(
      "connectionstatechange",
      this.#onConnectionState,
    );
    this.#connection.removeEventListener(
      "iceconnectionstatechange",
      this.#onIceConnectionState,
    );
    this.#clearIceDisconnectDeadline();
    this.#dtlsTransport?.removeEventListener("error", this.#onDtlsError);
    this.#dtlsTransport?.removeEventListener(
      "statechange",
      this.#onDtlsStateChange,
    );
    this.#dtlsTransport = undefined;
    this.#connection.close();
    this.#emitLogicalState();
  }

  /**
   * Retire physical transport immediately while preserving only the DTLS
   * hard-upgrade tombstone until fallback commits or the attempt ends.
   */
  public retireForFallback(): void {
    if (this.#closed || this.#retiredForFallback) return;
    this.#retiredForFallback = true;
    this.#softFailurePending = true;
    this.#clearOpenDeadline();
    this.#clearControlWritableTimer();
    if (this.#rateRecoveryTimer !== undefined) {
      this.#environment.clearTimeout(this.#rateRecoveryTimer);
      this.#rateRecoveryTimer = undefined;
    }
    for (const lane of Object.values(this.#lanes)) this.#closeLane(lane);
    this.#recordListeners.clear();
    this.#connection.removeEventListener("datachannel", this.#onDataChannel);
    this.#connection.removeEventListener("icecandidate", this.#onIceCandidate);
    this.#connection.removeEventListener("track", this.#onTrack);
    this.#connection.removeEventListener(
      "connectionstatechange",
      this.#onConnectionState,
    );
    this.#connection.removeEventListener(
      "iceconnectionstatechange",
      this.#onIceConnectionState,
    );
    this.#clearIceDisconnectDeadline();
    this.#connection.close();
    this.#emitLogicalState();
  }

  readonly #onDataChannel = (event: RTCDataChannelEvent): void => {
    if (this.#closed) return;
    const channel = event.channel;
    const lane = laneForChannel(channel);
    if (lane === undefined || this.#lanes[lane].channel !== undefined) {
      this.#failHard("CHANNEL_CONTRACT_MISMATCH");
      return;
    }
    if (!isConformingMemberChannel(channel, lane)) {
      this.#failHard("CHANNEL_CONTRACT_MISMATCH");
      return;
    }
    channel.binaryType = "arraybuffer";
    const state = this.#lanes[lane];
    state.channel = channel;
    channel.bufferedAmountLowThreshold =
      W10_DATA_CHANNEL_BACKPRESSURE_V1[lane].lowByteLength;
    channel.addEventListener("message", this.#messageHandler(lane));
    channel.addEventListener("open", this.#openHandler(lane));
    channel.addEventListener("close", this.#closeHandler(lane));
    channel.addEventListener("bufferedamountlow", this.#lowHandler(lane));
    if (channel.readyState === "open") this.#onChannelOpen();
  };

  readonly #onIceCandidate = (event: RTCPeerConnectionIceEvent): void => {
    if (this.#closed) return;
    const candidate = event.candidate;
    if (candidate === null || candidate.candidate.length === 0) {
      this.#options.onSignal({ kind: "ice", data: null });
      return;
    }
    this.#options.onSignal({ kind: "ice", data: candidate.candidate });
  };

  readonly #onTrack = (): void => {
    this.#failHard("CHANNEL_CONTRACT_MISMATCH");
  };

  readonly #onConnectionState = (): void => {
    if (this.#closed) return;
    const state = this.#connection.connectionState;
    if (state === "failed" || state === "closed") {
      if (this.#memberReady) {
        this.#failOperational(null);
      } else if (
        (this.#connection.iceConnectionState === "failed" ||
          this.#connection.iceConnectionState === "closed") &&
        this.#dtlsTransport !== undefined &&
        this.#dtlsTransport.state !== "failed"
      ) {
        this.#failOperational(null);
      } else {
        this.#failHard("DTLS_FAILURE_UNCLASSIFIED");
      }
    }
  };

  readonly #onIceConnectionState = (): void => {
    if (this.#closed) return;
    const state = this.#connection.iceConnectionState;
    if (state === "connected" || state === "completed") {
      this.#clearIceDisconnectDeadline();
      return;
    }
    if (state === "disconnected") {
      if (!this.#memberReady || this.#iceDisconnectTimer !== undefined) return;
      this.#iceDisconnectTimer = this.#environment.setTimeout(() => {
        this.#iceDisconnectTimer = undefined;
        this.#failOperational("PRIMARY_ICE_DISCONNECT_TIMEOUT");
      }, PROTOCOL_CONSTANTS.T_DISC_GRACE);
      return;
    }
    if (state === "failed" || state === "closed") {
      this.#clearIceDisconnectDeadline();
      if (
        this.#memberReady ||
        (this.#dtlsTransport !== undefined &&
          this.#dtlsTransport.state !== "failed")
      ) {
        this.#failOperational(null);
      } else {
        this.#failHard("DTLS_FAILURE_UNCLASSIFIED");
      }
    }
  };

  readonly #onDtlsError = (event: RTCErrorEvent): void => {
    if (event.error.errorDetail === "fingerprint-failure") {
      this.#failHard("DTLS_CERTIFICATE_MISMATCH");
    } else if (this.#memberReady) {
      this.#failOperational(null);
    } else {
      this.#failHard("DTLS_FAILURE_UNCLASSIFIED");
    }
  };

  readonly #onDtlsStateChange = (): void => {
    if (this.#dtlsTransport?.state !== "failed") return;
    if (this.#memberReady) this.#failOperational(null);
    else this.#failHard("DTLS_FAILURE_UNCLASSIFIED");
  };

  #createLane(lane: W10DataChannelLane): W10MemberLane {
    return {
      lane,
      receiver: createW10DataChannelLaneReceiver({ lane }),
      messages: new Set(),
      states: new Set(),
      writable: new Set(),
      outbound: [],
      nextOutboundMessageId: 1,
      lastOutboundMessageId: 0,
      pendingByteLength: 0,
      lastInboundMessageId: 0,
      paused: false,
    };
  }

  #listenState(
    lane: W10MemberLane,
    listener: (state: W10LogicalLaneState) => void,
  ): () => void {
    lane.states.add(listener);
    listener(this.#logicalState());
    return () => lane.states.delete(listener);
  }

  #logicalState(): W10LogicalLaneState {
    if (this.#closed || this.#softFailurePending) return "closed";
    return this.#applicationReady ? "open" : "connecting";
  }

  #emitLogicalState(): void {
    const state = this.#logicalState();
    for (const lane of Object.values(this.#lanes)) {
      for (const listener of lane.states) listener(state);
    }
  }

  #messageHandler(lane: W10DataChannelLane): (event: MessageEvent) => void {
    return lane === "control"
      ? this.#onControlMessage
      : this.#onWhiteboardMessage;
  }

  #openHandler(lane: W10DataChannelLane): () => void {
    return lane === "control" ? this.#onControlOpen : this.#onWhiteboardOpen;
  }

  #closeHandler(lane: W10DataChannelLane): () => void {
    return lane === "control" ? this.#onControlClose : this.#onWhiteboardClose;
  }

  #lowHandler(lane: W10DataChannelLane): () => void {
    return lane === "control" ? this.#onControlLow : this.#onWhiteboardLow;
  }

  readonly #onControlMessage = (event: MessageEvent): void =>
    this.#receive(this.#lanes.control, event.data);
  readonly #onWhiteboardMessage = (event: MessageEvent): void =>
    this.#receive(this.#lanes.whiteboard, event.data);
  readonly #onControlOpen = (): void => this.#onChannelOpen();
  readonly #onWhiteboardOpen = (): void => this.#onChannelOpen();
  readonly #onControlClose = (): void => this.#requiredChannelClosed();
  readonly #onWhiteboardClose = (): void => this.#requiredChannelClosed();
  readonly #onControlLow = (): void => {
    this.#resume(this.#lanes.control);
    this.#tryReleaseRateRecovery();
  };
  readonly #onWhiteboardLow = (): void => this.#resume(this.#lanes.whiteboard);

  #onChannelOpen(): void {
    if (this.#closed || this.#softFailurePending) return;
    const control = this.#lanes.control.channel;
    const whiteboard = this.#lanes.whiteboard.channel;
    if (control?.readyState !== "open" || whiteboard?.readyState !== "open") {
      return;
    }
    if (this.#helloSent) return;
    this.#armOpenDeadline();
    this.#helloSent = true;
    const sent = this.#enqueue(
      this.#lanes.control,
      0,
      W10_DATA_CHANNEL_KINDS.hello,
      encodeW10VersionRangePayload(1, 1),
    );
    if (sent === false) this.#failLocal("control-queue-overflow");
  }

  #receive(lane: W10MemberLane, value: unknown): void {
    if (this.#closed || this.#softFailurePending) return;
    if (typeof value === "string") {
      this.#failHard("FRAME_INTEGRITY");
      return;
    }
    if (!(value instanceof ArrayBuffer)) {
      this.#failOperational("DATA_CHANNEL_BINARY_UNAVAILABLE");
      return;
    }
    const bytes = new Uint8Array(value);
    let decoded: ReturnType<typeof decodeW10DataChannelFrame>;
    try {
      decoded = decodeW10DataChannelFrame(bytes, lane.lane);
    } catch {
      this.#failHard("FRAME_INTEGRITY");
      return;
    }
    if (
      (!this.#applicationReady && decoded.wireVersion !== 0) ||
      (this.#applicationReady &&
        decoded.wireVersion !== W10_DATA_CHANNEL_V1.wireVersion)
    ) {
      this.#failHard("VERSION_MISMATCH");
      return;
    }
    if (
      this.#applicationReady &&
      lane.lane === "whiteboard" &&
      !this.#admitted
    ) {
      this.#failHard("PRE_ADMISSION_LANE");
      return;
    }
    let partial = lane.partial;
    if (partial === undefined) {
      if (
        decoded.chunkIndex !== 0 ||
        decoded.messageId !== lane.receiver.expectedMessageId
      ) {
        this.#failHard("FRAME_INTEGRITY");
        return;
      }
      partial = {
        messageId: decoded.messageId,
        wireVersion: decoded.wireVersion,
        kind: decoded.kind,
        chunkCount: decoded.chunkCount,
        totalPayloadByteLength: decoded.totalPayloadByteLength,
        frames: [],
      };
      lane.partial = partial;
      partial.absoluteTimer = this.#environment.setTimeout(
        () => this.#failOperational("REASSEMBLY_TIMEOUT"),
        W10_DATA_CHANNEL_REASSEMBLY_V1.absoluteTimeoutMs,
      );
    }
    if (
      decoded.messageId !== partial.messageId ||
      decoded.wireVersion !== partial.wireVersion ||
      decoded.kind !== partial.kind ||
      decoded.chunkCount !== partial.chunkCount ||
      decoded.totalPayloadByteLength !== partial.totalPayloadByteLength ||
      decoded.chunkIndex !== partial.frames.length
    ) {
      this.#failHard("FRAME_INTEGRITY");
      return;
    }
    partial.frames.push(Uint8Array.from(bytes));
    if (partial.idleTimer !== undefined)
      this.#environment.clearTimeout(partial.idleTimer);
    partial.idleTimer = this.#environment.setTimeout(
      () => this.#failOperational("REASSEMBLY_TIMEOUT"),
      W10_DATA_CHANNEL_REASSEMBLY_V1.inactivityTimeoutMs,
    );
    if (partial.frames.length !== partial.chunkCount) return;

    this.#clearPartial(lane);
    let message: ReturnType<W10DataChannelLaneReceiver["receive"]>;
    try {
      message = lane.receiver.receive(
        partial.frames,
        this.#applicationReady ? W10_DATA_CHANNEL_V1.wireVersion : 0,
      );
    } catch {
      this.#failHard("FRAME_INTEGRITY");
      return;
    }
    lane.lastInboundMessageId = message.messageId;
    if (!this.#applicationReady) {
      this.#receiveNegotiation(message);
      return;
    }
    if (lane.lane === "control") {
      this.#receiveControlRecord(message.payload);
      return;
    }
    const logical = Object.freeze({
      messageId: message.messageId,
      payload: Uint8Array.from(message.payload),
    });
    for (const listener of lane.messages) listener(logical);
  }

  #receiveNegotiation(message: {
    readonly lane: W10DataChannelLane;
    readonly kind: number;
    readonly messageId: number;
    readonly payload: Uint8Array;
  }): void {
    if (message.lane !== "control") {
      this.#failHard("VERSION_MISMATCH");
      return;
    }
    if (message.kind === W10_DATA_CHANNEL_KINDS.hello) {
      if (message.messageId !== 1 || this.#peerHelloSeen) {
        this.#failHard("FRAME_INTEGRITY");
        return;
      }
      this.#peerHelloSeen = true;
      let response: ReturnType<typeof respondToW10VersionHello>;
      try {
        response = respondToW10VersionHello({
          localMinimum: 1,
          localMaximum: 1,
          helloPayload: message.payload,
        });
      } catch {
        this.#failHard("FRAME_INTEGRITY");
        return;
      }
      const kind =
        response.type === "ready"
          ? W10_DATA_CHANNEL_KINDS.ready
          : W10_DATA_CHANNEL_KINDS.reject;
      const sent = this.#enqueue(
        this.#lanes.control,
        0,
        kind,
        response.payload,
      );
      if (sent === false) {
        this.#failLocal("control-queue-overflow");
        return;
      }
      if (response.type === "reject") {
        this.#failHard("VERSION_MISMATCH");
        return;
      }
      this.#readySentVersion = response.selectedVersion;
      this.#finishNegotiationIfReady();
      return;
    }
    if (message.kind === W10_DATA_CHANNEL_KINDS.ready) {
      if (
        message.messageId !== 2 ||
        !this.#peerHelloSeen ||
        this.#peerReadySeen
      ) {
        this.#failHard("FRAME_INTEGRITY");
        return;
      }
      let selected: number;
      try {
        selected = decodeW10ReadyPayload(message.payload);
      } catch {
        this.#failHard("FRAME_INTEGRITY");
        return;
      }
      if (selected !== this.#readySentVersion) {
        this.#failHard("VERSION_MISMATCH");
        return;
      }
      this.#peerReadySeen = true;
      this.#finishNegotiationIfReady();
      return;
    }
    if (message.kind === W10_DATA_CHANNEL_KINDS.reject) {
      try {
        decodeW10RejectPayload(message.payload);
      } catch {
        this.#failHard("FRAME_INTEGRITY");
        return;
      }
      this.#failHard("VERSION_MISMATCH");
      return;
    }
    this.#failHard("VERSION_MISMATCH");
  }

  #finishNegotiationIfReady(): void {
    if (
      this.#softFailurePending ||
      this.#applicationReady ||
      !this.#peerHelloSeen ||
      !this.#peerReadySeen ||
      !this.#localReadyFlushed ||
      this.#readySentVersion !== W10_DATA_CHANNEL_V1.wireVersion ||
      this.#dtlsObservationStarted
    ) {
      return;
    }
    this.#dtlsObservationStarted = true;
    void this.#observeEstablishedDtlsBinding();
  }

  async #observeEstablishedDtlsBinding(): Promise<void> {
    this.#dtlsObservationPending = true;
    try {
      await this.#readEstablishedDtlsBinding();
    } finally {
      this.#dtlsObservationPending = false;
      this.#finishPendingSoftFailure();
    }
  }

  async #readEstablishedDtlsBinding(): Promise<void> {
    const transport = this.#dtlsTransport;
    if (this.#closed || transport?.state !== "connected") {
      this.#failOperational("DTLS_OBSERVATION_UNAVAILABLE");
      return;
    }
    let report: RTCStatsReport;
    try {
      report = await this.#connection.getStats();
    } catch {
      if (!this.#closed && transport.state === "connected") {
        this.#failOperational("DTLS_OBSERVATION_UNAVAILABLE");
      }
      return;
    }
    if (this.#closed) return;
    if (transport.state !== "connected") {
      this.#onDtlsStateChange();
      if (!this.#closed) this.#failOperational("DTLS_OBSERVATION_UNAVAILABLE");
      return;
    }
    let binding: W10EstablishedDtlsBinding;
    try {
      binding = await readEstablishedDtlsBinding(
        report,
        this.#environment.crypto.subtle,
      );
    } catch (cause) {
      if (cause instanceof DtlsCertificateMismatchError) {
        this.#failHard("DTLS_CERTIFICATE_MISMATCH");
      } else if (cause instanceof DtlsTransportFailedError) {
        if (this.#memberReady) this.#failOperational(null);
        else this.#failHard("DTLS_FAILURE_UNCLASSIFIED");
      } else if (transport.state === "connected") {
        this.#failOperational("DTLS_OBSERVATION_UNAVAILABLE");
      }
      return;
    }
    if (this.#closed || this.#pendingSoftFailure !== undefined) return;
    if (transport.state !== "connected") {
      this.#onDtlsStateChange();
      if (!this.#closed) this.#failOperational("DTLS_OBSERVATION_UNAVAILABLE");
      return;
    }
    this.#applicationReady = true;
    this.#controlTokens = PROTOCOL_CONSTANTS.LIMIT_MSGS.burst;
    this.#controlLastRefill = this.#environment.now();
    this.#clearOpenDeadline();
    this.#emitLogicalState();
    this.#options.onReady(this.#carrier, binding);
  }

  #sendControlRecord(
    type: string,
    payload: Readonly<Record<string, unknown>>,
  ): W10PageSendResult {
    if (this.#softFailurePending)
      return this.#terminalSendResult ?? PAUSED_SEND_RESULT;
    if (!this.#applicationReady) return PAUSED_SEND_RESULT;
    if (!isKebabCaseType(type)) {
      this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
      return HARD_PROTOCOL_SEND_RESULT;
    }
    const sequence = this.#nextControlSequence;
    if (!Number.isSafeInteger(sequence) || sequence <= 0) {
      this.#failLocal("counter-exhausted");
      return localFailedSendResult("counter-exhausted");
    }
    let bytes: Uint8Array;
    try {
      if (
        !isW10ExactControlType(type) &&
        isRetainedClientControlType(type) &&
        !clientMessageSchema.safeParse({
          v: 1,
          type,
          seq: sequence,
          payload,
        }).success
      ) {
        throw new TypeError("invalid retained control record");
      }
      bytes = isW10ExactControlType(type)
        ? encodeW10ExactControlRecord(sequence, {
            type,
            payload,
          } as unknown as W10ExactControlRecord)
        : new TextEncoder().encode(
            JSON.stringify({ v: 1, type, seq: sequence, payload }),
          );
    } catch {
      this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
      return HARD_PROTOCOL_SEND_RESULT;
    }
    const exemptionKey = rateExemptionKey(type, payload);
    const isDirtyRecoveryReplacement =
      this.#rateRecoveryPending && type === "whiteboard-resync-needed";
    if (
      this.#rateRecoveryPending &&
      exemptionKey !== undefined &&
      (this.#usedRateExemptions.has(exemptionKey) ||
        this.#controlIntents.some(
          (intent) => intent.exemptionKey === exemptionKey,
        ))
    ) {
      return PAUSED_SEND_RESULT;
    }
    let estimatedByteLength: number;
    try {
      estimatedByteLength = chunkW10DataChannelMessage({
        lane: "control",
        wireVersion: W10_DATA_CHANNEL_V1.wireVersion,
        kind: W10_DATA_CHANNEL_KINDS.control,
        messageId: this.#lanes.control.nextOutboundMessageId,
        payload: bytes,
      }).reduce((total, frame) => total + frame.byteLength, 0);
    } catch {
      this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
      return HARD_PROTOCOL_SEND_RESULT;
    }
    const previousStrict = this.#memberReady
      ? undefined
      : this.#lastStrictControlIntent;
    const dirtyReplacementIndex =
      type === "whiteboard-resync-needed"
        ? this.#controlIntents.findIndex(
            (intent) => intent.type === "whiteboard-resync-needed",
          )
        : -1;
    const strictReplacementIndex =
      this.#rateRecoveryPending && previousStrict !== undefined
        ? this.#controlIntents.findIndex((intent) => intent === previousStrict)
        : -1;
    const replacementIndex =
      dirtyReplacementIndex >= 0
        ? dirtyReplacementIndex
        : strictReplacementIndex;
    const replacedIntent =
      replacementIndex < 0 ? undefined : this.#controlIntents[replacementIndex];
    const reconstructionReplacementIndex = isDirtyRecoveryReplacement
      ? this.#rateRecoveryReconstruction.findIndex(
          (intent) => intent.type === "whiteboard-resync-needed",
        )
      : -1;
    const reconstructedDirty =
      reconstructionReplacementIndex < 0
        ? undefined
        : this.#rateRecoveryReconstruction[reconstructionReplacementIndex];
    const reconstructedStrict =
      this.#rateRecoveryPending &&
      previousStrict !== undefined &&
      this.#rateRecoveryReconstruction.includes(previousStrict)
        ? previousStrict
        : undefined;
    if (
      type === "whiteboard-resync-needed" &&
      [replacedIntent, reconstructedDirty].some(
        (candidate) =>
          candidate !== undefined &&
          Number(payload.pageDirtyGeneration) <=
            Number(candidate.payload.pageDirtyGeneration),
      )
    ) {
      return this.#terminalSendResult ?? ACCEPTED_SEND_RESULT;
    }
    const reconstructionPendingByteLength =
      this.#rateRecoveryReconstruction.reduce(
        (total, intent) => total + intent.estimatedByteLength,
        0,
      );
    const replacementCredit = [
      ...new Set(
        [replacedIntent, reconstructedDirty, reconstructedStrict].filter(
          (intent): intent is W10ControlIntent => intent !== undefined,
        ),
      ),
    ].reduce((total, intent) => total + intent.estimatedByteLength, 0);
    if (
      this.#lanes.control.pendingByteLength +
        this.#controlIntentPendingByteLength +
        reconstructionPendingByteLength +
        estimatedByteLength -
        replacementCredit >
      W10_DATA_CHANNEL_BACKPRESSURE_V1.control.pendingByteLength
    ) {
      this.#failLocal("control-queue-overflow");
      return localFailedSendResult("control-queue-overflow");
    }
    const intent: W10ControlIntent = Object.freeze({
      type,
      payload: Object.freeze({ ...payload }),
      estimatedByteLength,
      ...(exemptionKey === undefined ? {} : { exemptionKey }),
    });
    if (isDirtyRecoveryReplacement) {
      if (reconstructionReplacementIndex < 0) {
        this.#rateRecoveryReconstruction.push(intent);
      } else {
        this.#rateRecoveryReconstruction[reconstructionReplacementIndex] =
          intent;
      }
      if (!this.#memberReady) this.#lastStrictControlIntent = intent;
      this.#latestDirtyControlIntent = intent;
      return this.#terminalSendResult ?? ACCEPTED_SEND_RESULT;
    }
    if (!this.#memberReady) {
      this.#lastStrictControlIntent = intent;
      if (this.#rateRecoveryPending && previousStrict !== undefined) {
        this.#rateRecoveryReconstruction =
          this.#rateRecoveryReconstruction.filter(
            (candidate) => candidate !== previousStrict,
          );
      }
    }
    if (replacementIndex < 0) {
      if (this.#rateRecoveryPending && exemptionKey !== undefined) {
        const firstOrdinaryIndex = this.#controlIntents.findIndex(
          (candidate) => candidate.exemptionKey === undefined,
        );
        if (firstOrdinaryIndex < 0) this.#controlIntents.push(intent);
        else this.#controlIntents.splice(firstOrdinaryIndex, 0, intent);
      } else {
        this.#controlIntents.push(intent);
      }
      this.#controlIntentPendingByteLength += estimatedByteLength;
    } else {
      this.#controlIntents[replacementIndex] = intent;
      this.#controlIntentPendingByteLength +=
        estimatedByteLength - (replacedIntent?.estimatedByteLength ?? 0);
    }
    if (type === "whiteboard-resync-needed") {
      this.#latestDirtyControlIntent = intent;
    }
    this.#pump();
    return this.#terminalSendResult ?? ACCEPTED_SEND_RESULT;
  }

  #controlPermission(
    type: string,
    payload: Readonly<Record<string, unknown>>,
  ): { readonly atMs: number; readonly exemptionKey?: string } | undefined {
    const atMs = this.#environment.now();
    const exemptionKey = rateExemptionKey(type, payload);
    if (
      exemptionKey !== undefined &&
      !this.#usedRateExemptions.has(exemptionKey)
    ) {
      return { atMs, exemptionKey };
    }
    if (this.#rateRecoveryPending) return undefined;
    this.#refillControlTokens(atMs);
    if (this.#controlTokens < 1) {
      const missing = 1 - this.#controlTokens;
      this.#scheduleControlWritableAt(
        atMs + (missing * 1_000) / PROTOCOL_CONSTANTS.LIMIT_MSGS.ratePerSecond,
      );
      return undefined;
    }
    if (isW10SyncWorkType(type) && atMs < this.#controlSyncNotBefore) {
      this.#scheduleControlWritableAt(this.#controlSyncNotBefore);
      return undefined;
    }
    return { atMs };
  }

  #sendWhiteboard(payload: Uint8Array): W10PageSendResult {
    if (this.#softFailurePending)
      return this.#terminalSendResult ?? PAUSED_SEND_RESULT;
    const lane = this.#lanes.whiteboard;
    if (
      this.#whiteboardWritableNotificationPending ||
      !this.#admitted ||
      lane.paused ||
      lane.outbound.length > 0
    ) {
      return PAUSED_SEND_RESULT;
    }
    const messageId = this.#sendApplication("whiteboard", payload);
    if (messageId === false)
      return this.#terminalSendResult ?? PAUSED_SEND_RESULT;
    return this.#terminalSendResult ?? ACCEPTED_SEND_RESULT;
  }

  #materializeNextControlIntent(): number | false {
    const intent = this.#controlIntents[0];
    const lane = this.#lanes.control;
    const channel = lane.channel;
    if (intent === undefined || channel?.readyState !== "open") return false;
    const permission = this.#controlPermission(intent.type, intent.payload);
    if (permission === undefined) return false;
    if (
      this.#nextControlSequence > Number.MAX_SAFE_INTEGER ||
      lane.nextOutboundMessageId > UINT32_MAX
    ) {
      this.#failLocal("counter-exhausted");
      return false;
    }
    let bytes: Uint8Array;
    let frames: readonly Uint8Array[];
    try {
      bytes = isW10ExactControlType(intent.type)
        ? encodeW10ExactControlRecord(this.#nextControlSequence, {
            type: intent.type,
            payload: intent.payload,
          } as unknown as W10ExactControlRecord)
        : new TextEncoder().encode(
            JSON.stringify({
              v: 1,
              type: intent.type,
              seq: this.#nextControlSequence,
              payload: intent.payload,
            }),
          );
      frames = chunkW10DataChannelMessage({
        lane: "control",
        wireVersion: W10_DATA_CHANNEL_V1.wireVersion,
        kind: W10_DATA_CHANNEL_KINDS.control,
        messageId: lane.nextOutboundMessageId,
        payload: bytes,
      });
    } catch {
      this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
      return false;
    }
    const firstFrame = frames[0];
    if (firstFrame === undefined) {
      this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
      return false;
    }
    if (
      channel.bufferedAmount + firstFrame.byteLength >
      W10_DATA_CHANNEL_BACKPRESSURE_V1.control.highByteLength
    ) {
      this.#pause(lane);
      return false;
    }
    const byteLength = frames.reduce(
      (total, frame) => total + frame.byteLength,
      0,
    );
    if (
      lane.pendingByteLength + byteLength >
      W10_DATA_CHANNEL_BACKPRESSURE_V1.control.pendingByteLength
    ) {
      this.#failLocal("control-queue-overflow");
      return false;
    }
    this.#controlIntents.shift();
    this.#controlIntentPendingByteLength -= intent.estimatedByteLength;
    const messageId = lane.nextOutboundMessageId;
    lane.nextOutboundMessageId = messageId + 1;
    lane.lastOutboundMessageId = messageId;
    lane.pendingByteLength += byteLength;
    lane.outbound.push({ messageId, frames, byteLength, nextFrame: 0 });
    this.#armSendProgressDeadline(lane);
    this.#nextControlSequence += 1;
    if (permission.exemptionKey === undefined) {
      this.#controlTokens = Math.max(0, this.#controlTokens - 1);
      if (this.#controlTokens < 1) {
        this.#scheduleControlWritableAt(
          permission.atMs +
            ((1 - this.#controlTokens) * 1_000) /
              PROTOCOL_CONSTANTS.LIMIT_MSGS.ratePerSecond,
        );
      }
      if (isW10SyncWorkType(intent.type)) {
        this.#controlSyncNotBefore =
          permission.atMs + PROTOCOL_CONSTANTS.LIMIT_SYNC_REQUEST.windowMs;
      }
      if (!this.#memberReady) this.#lastStrictControlIntent = intent;
    } else {
      this.#usedRateExemptions.add(permission.exemptionKey);
    }
    return frames.length;
  }

  #receiveControlRecord(payload: Uint8Array): void {
    let parsed: unknown;
    try {
      parsed = parseW10ControlJsonBytes(payload);
    } catch {
      this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
      return;
    }
    if (!isPlainRecord(parsed)) {
      this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
      return;
    }
    let sequence: number;
    let record: W10ControlRecord;
    let knownType = true;
    if (isW10ExactControlType(parsed.type)) {
      try {
        const decoded = decodeW10ExactControlRecord(
          new TextEncoder().encode(
            JSON.stringify({
              v: parsed.v,
              type: parsed.type,
              seq: parsed.seq,
              payload: parsed.payload,
            }),
          ),
        );
        sequence = decoded.seq;
        record = Object.freeze({
          type: decoded.type,
          payload: decoded.payload as unknown as Readonly<
            Record<string, unknown>
          >,
        });
      } catch {
        this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
        return;
      }
    } else {
      if (
        parsed.v !== 1 ||
        !isKebabCaseType(parsed.type) ||
        !Number.isSafeInteger(parsed.seq) ||
        (parsed.seq as number) <= 0 ||
        !isPlainRecord(parsed.payload)
      ) {
        this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
        return;
      }
      sequence = parsed.seq as number;
      record = Object.freeze({
        type: parsed.type,
        payload: Object.freeze({ ...parsed.payload }),
      });
      knownType = isRetainedServerControlType(record.type);
      if (knownType && !serverMessageSchema.safeParse(parsed).success) {
        this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
        return;
      }
      if (
        record.type === "error" &&
        record.payload.code === "RATE_LIMITED" &&
        !isExactRateLimitedPayload(record.payload)
      ) {
        this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
        return;
      }
    }
    if (
      (!this.#hasObservedControlRecord && sequence !== 1) ||
      (this.#hasObservedControlRecord && sequence <= this.#lastControlSequence)
    ) {
      this.#failHard("CONTROL_PROTOCOL_INTEGRITY");
      return;
    }
    this.#hasObservedControlRecord = true;
    this.#lastControlSequence = sequence;
    if (!knownType) {
      if (!this.#admitted) {
        this.#failHard("PRE_ADMISSION_CONTROL");
        return;
      }
      this.#sendControlRecord("error", {
        code: "UNSUPPORTED_TYPE",
        message: `unsupported message type: ${record.type}`,
        fatal: false,
        refSeq: sequence,
      });
      return;
    }
    if (record.type === "error" && record.payload.code === "RATE_LIMITED") {
      if (this.#rateRecoveryStrikeObserved) {
        this.#failPolicy("RATE_LIMITED");
        return;
      }
      this.#rateRecoveryStrikeObserved = true;
      this.#beginRateRecovery();
      for (const listener of this.#recordListeners) listener(record);
      return;
    }
    if (!this.#memberReady) {
      this.#lastStrictControlIntent = undefined;
      this.#rateRecoveryReconstruction =
        this.#rateRecoveryReconstruction.filter(
          (intent) => intent.type === "whiteboard-resync-needed",
        );
    }
    if (record.type === "whiteboard-resync-request") {
      this.#latestDirtyControlIntent = undefined;
      this.#rateRecoveryReconstruction =
        this.#rateRecoveryReconstruction.filter(
          (intent) => intent.type !== "whiteboard-resync-needed",
        );
      if (this.#memberReady && !this.#admitted) {
        const held = this.#heldPostReadyControlRecord;
        if (
          held !== undefined &&
          JSON.stringify(held.payload) === JSON.stringify(record.payload)
        ) {
          return;
        }
        if (held !== undefined) {
          this.#failHard("PRE_ADMISSION_CONTROL");
          return;
        }
        this.#heldPostReadyControlRecord = record;
        return;
      }
    }
    for (const listener of this.#recordListeners) listener(record);
  }

  #beginRateRecovery(): void {
    if (this.#rateRecoveryPending || this.#closed) return;
    const receiptInstant = this.#environment.now();
    this.#rateRecoveryPending = true;
    this.#controlTokens = 0;
    this.#controlLastRefill = receiptInstant;
    this.#rateRecoveryReleaseAt =
      receiptInstant + PROTOCOL_CONSTANTS.LIMIT_SYNC_REQUEST.windowMs;
    this.#controlSyncNotBefore = Math.max(
      this.#controlSyncNotBefore,
      this.#rateRecoveryReleaseAt,
    );
    this.#rateRecoveryDrainThroughMessageId =
      this.#lanes.control.lastOutboundMessageId;
    const controlChannel = this.#lanes.control.channel;
    if (controlChannel !== undefined) {
      controlChannel.bufferedAmountLowThreshold = 0;
    }
    const retainedExemptions = this.#controlIntents.filter(
      (intent) =>
        intent.exemptionKey !== undefined &&
        !this.#usedRateExemptions.has(intent.exemptionKey),
    );
    const reconstruction: W10ControlIntent[] = [];
    if (!this.#memberReady && this.#lastStrictControlIntent !== undefined) {
      reconstruction.push(this.#lastStrictControlIntent);
    }
    if (
      this.#latestDirtyControlIntent !== undefined &&
      !reconstruction.includes(this.#latestDirtyControlIntent)
    ) {
      reconstruction.push(this.#latestDirtyControlIntent);
    }
    this.#controlIntents = retainedExemptions;
    this.#controlIntentPendingByteLength = retainedExemptions.reduce(
      (total, intent) => total + intent.estimatedByteLength,
      0,
    );
    this.#rateRecoveryReconstruction = reconstruction;
    this.#clearControlWritableTimer();
    this.#rateRecoveryTimer = this.#environment.setTimeout(() => {
      this.#rateRecoveryTimer = undefined;
      this.#tryReleaseRateRecovery();
    }, PROTOCOL_CONSTANTS.LIMIT_SYNC_REQUEST.windowMs);
    this.#pump();
  }

  #tryReleaseRateRecovery(): void {
    if (!this.#rateRecoveryPending || this.#closed) return;
    const now = this.#environment.now();
    if (now < this.#rateRecoveryReleaseAt) return;
    if (
      this.#lanes.control.outbound.some(
        (job) => job.messageId <= this.#rateRecoveryDrainThroughMessageId,
      )
    ) {
      return;
    }
    const channel = this.#lanes.control.channel;
    if (channel !== undefined && channel.bufferedAmount > 0) {
      return;
    }
    this.#rateRecoveryPending = false;
    if (channel !== undefined) {
      channel.bufferedAmountLowThreshold =
        W10_DATA_CHANNEL_BACKPRESSURE_V1.control.lowByteLength;
    }
    this.#controlTokens = 0;
    this.#controlLastRefill = now;
    const existing = new Set(this.#controlIntents);
    for (const intent of this.#rateRecoveryReconstruction) {
      if (existing.has(intent)) continue;
      this.#controlIntents.push(intent);
      this.#controlIntentPendingByteLength += intent.estimatedByteLength;
    }
    this.#rateRecoveryReconstruction = [];
    const firstTokenAt =
      now + 1_000 / PROTOCOL_CONSTANTS.LIMIT_MSGS.ratePerSecond;
    this.#scheduleControlWritableAt(firstTokenAt);
  }

  #refillControlTokens(now: number): void {
    const elapsed = Math.max(0, now - this.#controlLastRefill);
    this.#controlTokens = Math.min(
      PROTOCOL_CONSTANTS.LIMIT_MSGS.burst,
      this.#controlTokens +
        (elapsed * PROTOCOL_CONSTANTS.LIMIT_MSGS.ratePerSecond) / 1_000,
    );
    this.#controlLastRefill = now;
  }

  #sendApplication(
    lane: W10DataChannelLane,
    payload: Uint8Array,
  ): number | false {
    if (!this.#applicationReady) return false;
    return this.#enqueue(
      this.#lanes[lane],
      W10_DATA_CHANNEL_V1.wireVersion,
      lane === "control"
        ? W10_DATA_CHANNEL_KINDS.control
        : W10_DATA_CHANNEL_KINDS.whiteboard,
      payload,
    );
  }

  #enqueue(
    lane: W10MemberLane,
    wireVersion: number,
    kind: number,
    payload: Uint8Array,
  ): number | false {
    if (lane.lane === "whiteboard" && lane.outbound.length > 0) return false;
    if (
      this.#closed ||
      lane.channel?.readyState !== "open" ||
      !(payload instanceof Uint8Array) ||
      payload.byteLength === 0
    ) {
      return false;
    }
    if (lane.nextOutboundMessageId > UINT32_MAX) {
      this.#failLocal("counter-exhausted");
      return false;
    }
    const messageId = lane.nextOutboundMessageId;
    let frames: readonly Uint8Array[];
    try {
      frames = chunkW10DataChannelMessage({
        lane: lane.lane,
        wireVersion,
        kind,
        messageId,
        payload,
      });
    } catch {
      if (
        lane.lane === "whiteboard" &&
        payload.byteLength > W10_DATA_CHANNEL_V1.maxWhiteboardMessageByteLength
      ) {
        this.#failOperational("WHITEBOARD_MESSAGE_TOO_LARGE");
      }
      return false;
    }
    const byteLength = frames.reduce(
      (total, frame) => total + frame.byteLength,
      0,
    );
    if (
      lane.lane === "control" &&
      lane.pendingByteLength + byteLength >
        W10_DATA_CHANNEL_BACKPRESSURE_V1.control.pendingByteLength
    ) {
      this.#failLocal("control-queue-overflow");
      return false;
    }
    lane.nextOutboundMessageId = messageId + 1;
    lane.lastOutboundMessageId = messageId;
    const wasIdle = lane.outbound.length === 0;
    lane.pendingByteLength += byteLength;
    lane.outbound.push({ messageId, frames, byteLength, nextFrame: 0 });
    if (wasIdle && !lane.paused) this.#armSendProgressDeadline(lane);
    this.#pump();
    return messageId;
  }

  #pump(): void {
    if (this.#closed || this.#softFailurePending || this.#pumping) return;
    this.#pumping = true;
    try {
      let controlFramesRemaining = this.#lanes.control.outbound.reduce(
        (total, job) => total + job.frames.length - job.nextFrame,
        0,
      );
      let controlIntentsRemaining = this.#controlIntents.length;
      let controlBlockedByGuard = false;
      while (!this.#closed) {
        const control = this.#lanes.control;
        if (
          controlFramesRemaining > 0 &&
          control.outbound.length > 0 &&
          !control.paused
        ) {
          if (!this.#sendNextFrame(control)) {
            if (!control.paused) break;
            controlFramesRemaining = 0;
          } else {
            controlFramesRemaining -= 1;
          }
          continue;
        }
        if (
          controlFramesRemaining === 0 &&
          control.outbound.length === 0 &&
          controlIntentsRemaining > 0 &&
          !control.paused
        ) {
          const frameCount = this.#materializeNextControlIntent();
          if (frameCount === false) {
            controlBlockedByGuard = !control.paused && !this.#closed;
            controlIntentsRemaining = 0;
          } else {
            controlIntentsRemaining -= 1;
            controlFramesRemaining = frameCount;
            continue;
          }
        }
        const whiteboard = this.#lanes.whiteboard;
        if (
          whiteboard.outbound.length === 0 ||
          whiteboard.paused ||
          this.#whiteboardYieldPending
        ) {
          if (
            controlFramesRemaining === 0 &&
            (control.outbound.length > 0 ||
              (controlIntentsRemaining > 0 && !controlBlockedByGuard)) &&
            !control.paused
          ) {
            this.#schedulePumpTurn();
          }
          break;
        }
        if (!this.#sendNextFrame(whiteboard)) break;
        if (
          whiteboard.outbound.length > 0 ||
          control.outbound.length > 0 ||
          (this.#controlIntents.length > 0 && !controlBlockedByGuard)
        )
          this.#schedulePumpTurn();
        break;
      }
    } finally {
      this.#pumping = false;
    }
  }

  #schedulePumpTurn(): void {
    if (this.#whiteboardYieldPending) return;
    this.#whiteboardYieldPending = true;
    this.#environment.setTimeout(() => {
      this.#whiteboardYieldPending = false;
      this.#pump();
    }, 0);
  }

  #sendNextFrame(lane: W10MemberLane): boolean {
    const job = lane.outbound[0];
    const channel = lane.channel;
    if (job === undefined || channel === undefined) return false;
    const frame = job.frames[job.nextFrame];
    if (frame === undefined) {
      this.#completeJob(lane, job);
      return true;
    }
    const limits = W10_DATA_CHANNEL_BACKPRESSURE_V1[lane.lane];
    if (channel.bufferedAmount + frame.byteLength > limits.highByteLength) {
      this.#pause(lane);
      return false;
    }
    try {
      channel.send(Uint8Array.from(frame));
    } catch {
      this.#failLocal(
        channel.readyState === "open"
          ? "send-threw-while-open"
          : "channel-closed",
      );
      return false;
    }
    this.#armSendProgressDeadline(lane);
    job.nextFrame += 1;
    if (job.nextFrame === job.frames.length) this.#completeJob(lane, job);
    return true;
  }

  #completeJob(lane: W10MemberLane, job: W10OutboundJob): void {
    if (lane.outbound[0] !== job) return;
    lane.outbound.shift();
    lane.pendingByteLength -= job.byteLength;
    if (lane.outbound.length === 0) this.#clearSendProgressDeadline(lane);
    if (lane.lane === "control" && job.messageId === 2) {
      this.#localReadyFlushed = true;
      this.#finishNegotiationIfReady();
    }
    if (lane.lane === "whiteboard") {
      this.#notifyWhiteboardWritable();
    } else {
      this.#tryReleaseRateRecovery();
    }
  }

  #pause(lane: W10MemberLane): void {
    if (lane.paused) return;
    lane.paused = true;
    this.#clearSendProgressDeadline(lane);
    lane.drainTimer = this.#environment.setTimeout(() => {
      lane.drainTimer = undefined;
      this.#failLocal("high-water-drain-expired");
    }, W10_DATA_CHANNEL_BACKPRESSURE_V1.drainTimeoutMs);
  }

  #isWhiteboardWritable(): boolean {
    const lane = this.#lanes.whiteboard;
    return (
      !this.#closed &&
      !this.#softFailurePending &&
      this.#applicationReady &&
      this.#admitted &&
      lane.channel?.readyState === "open" &&
      !lane.paused &&
      !this.#whiteboardWritableNotificationPending &&
      lane.outbound.length === 0
    );
  }

  #isControlWritable(): boolean {
    const lane = this.#lanes.control;
    if (
      this.#closed ||
      this.#softFailurePending ||
      !this.#applicationReady ||
      this.#rateRecoveryPending ||
      lane.channel?.readyState !== "open" ||
      lane.paused
    ) {
      return false;
    }
    this.#refillControlTokens(this.#environment.now());
    return this.#controlTokens >= 1;
  }

  #scheduleControlWritableAt(instant: number): void {
    if (this.#closed || this.#rateRecoveryPending) return;
    if (
      this.#controlWritableNotificationTimer !== undefined &&
      this.#controlWritableNotificationAt <= instant
    ) {
      return;
    }
    this.#clearControlWritableTimer();
    this.#controlWritableNotificationAt = instant;
    this.#controlWritableNotificationTimer = this.#environment.setTimeout(
      () => {
        this.#controlWritableNotificationTimer = undefined;
        this.#controlWritableNotificationAt = Number.POSITIVE_INFINITY;
        if (this.#closed || this.#rateRecoveryPending) return;
        const now = this.#environment.now();
        this.#refillControlTokens(now);
        if (this.#controlTokens < 1) {
          this.#scheduleControlWritableAt(
            now +
              ((1 - this.#controlTokens) * 1_000) /
                PROTOCOL_CONSTANTS.LIMIT_MSGS.ratePerSecond,
          );
          return;
        }
        this.#pump();
        if (!this.#isControlWritable()) return;
        for (const listener of this.#lanes.control.writable) listener();
      },
      Math.max(0, instant - this.#environment.now()),
    );
  }

  #clearControlWritableTimer(): void {
    if (this.#controlWritableNotificationTimer !== undefined) {
      this.#environment.clearTimeout(this.#controlWritableNotificationTimer);
      this.#controlWritableNotificationTimer = undefined;
    }
    this.#controlWritableNotificationAt = Number.POSITIVE_INFINITY;
  }

  #notifyWhiteboardWritable(): void {
    if (this.#whiteboardWritableNotificationPending) return;
    this.#whiteboardWritableNotificationPending = true;
    this.#environment.setTimeout(() => {
      this.#whiteboardWritableNotificationPending = false;
      if (!this.#isWhiteboardWritable()) return;
      for (const listener of this.#lanes.whiteboard.writable) listener();
    }, 0);
  }

  #resume(lane: W10MemberLane): void {
    if (!lane.paused || lane.channel === undefined) return;
    const low = W10_DATA_CHANNEL_BACKPRESSURE_V1[lane.lane].lowByteLength;
    if (lane.channel.bufferedAmount > low) return;
    lane.paused = false;
    if (lane.drainTimer !== undefined) {
      this.#environment.clearTimeout(lane.drainTimer);
      lane.drainTimer = undefined;
    }
    if (lane.outbound.length > 0) this.#armSendProgressDeadline(lane);
    this.#pump();
    if (lane.lane === "control") this.#tryReleaseRateRecovery();
  }

  async #flushMemberCandidates(): Promise<void> {
    const candidates = this.#queuedCandidates.splice(0);
    for (const candidate of candidates) {
      try {
        await this.#connection.addIceCandidate(candidate);
      } catch {
        this.#failHard("CHANNEL_CONTRACT_MISMATCH");
        return;
      }
    }
  }

  #installDtlsObserver(): boolean {
    const transport = this.#connection.sctp?.transport;
    if (transport === undefined || transport === null) {
      this.#failOperational("DTLS_OBSERVATION_UNAVAILABLE");
      return false;
    }
    try {
      transport.addEventListener("error", this.#onDtlsError);
      transport.addEventListener("statechange", this.#onDtlsStateChange);
    } catch {
      this.#failHard("DTLS_OBSERVER_UNSAFE");
      return false;
    }
    this.#dtlsTransport = transport;
    return true;
  }

  #armOpenDeadline(): void {
    this.#clearOpenDeadline();
    this.#openTimer = this.#environment.setTimeout(() => {
      this.#openTimer = undefined;
      if (this.#closed || this.#softFailurePending) return;
      const failure =
        this.#pendingSoftFailure ??
        Object.freeze({
          class: "operational" as const,
          cause: "CHANNEL_OPEN_TIMEOUT" as const,
        });
      this.#pendingSoftFailure = undefined;
      this.#emitFailure(failure);
    }, PROTOCOL_CONSTANTS.T_CONNECT);
  }

  #clearOpenDeadline(): void {
    if (this.#openTimer !== undefined)
      this.#environment.clearTimeout(this.#openTimer);
    this.#openTimer = undefined;
  }

  #clearIceDisconnectDeadline(): void {
    if (this.#iceDisconnectTimer !== undefined) {
      this.#environment.clearTimeout(this.#iceDisconnectTimer);
      this.#iceDisconnectTimer = undefined;
    }
  }

  #clearPartial(lane: W10MemberLane): void {
    const partial = lane.partial;
    lane.partial = undefined;
    if (partial?.idleTimer !== undefined)
      this.#environment.clearTimeout(partial.idleTimer);
    if (partial?.absoluteTimer !== undefined)
      this.#environment.clearTimeout(partial.absoluteTimer);
  }

  #closeLane(lane: W10MemberLane): void {
    this.#clearPartial(lane);
    this.#clearSendProgressDeadline(lane);
    if (lane.drainTimer !== undefined)
      this.#environment.clearTimeout(lane.drainTimer);
    lane.drainTimer = undefined;
    lane.outbound.splice(0);
    lane.pendingByteLength = 0;
    const channel = lane.channel;
    lane.channel = undefined;
    if (channel !== undefined) {
      channel.removeEventListener("message", this.#messageHandler(lane.lane));
      channel.removeEventListener("open", this.#openHandler(lane.lane));
      channel.removeEventListener("close", this.#closeHandler(lane.lane));
      channel.removeEventListener(
        "bufferedamountlow",
        this.#lowHandler(lane.lane),
      );
      channel.close();
    }
    lane.messages.clear();
    lane.writable.clear();
  }

  #armSendProgressDeadline(lane: W10MemberLane): void {
    this.#clearSendProgressDeadline(lane);
    if (
      this.#closed ||
      this.#softFailurePending ||
      lane.paused ||
      lane.outbound.length === 0
    ) {
      return;
    }
    lane.sendProgressTimer = this.#environment.setTimeout(() => {
      lane.sendProgressTimer = undefined;
      this.#failLocal("send-progress-timeout");
    }, PROTOCOL_CONSTANTS.T_CONNECT);
  }

  #clearSendProgressDeadline(lane: W10MemberLane): void {
    if (lane.sendProgressTimer !== undefined) {
      this.#environment.clearTimeout(lane.sendProgressTimer);
      lane.sendProgressTimer = undefined;
    }
  }

  #failOperational(cause: W10PageOperationalCause | null): void {
    if (this.#closed || this.#softFailurePending) return;
    if (cause === "WHITEBOARD_MESSAGE_TOO_LARGE") {
      this.#terminalSendResult = OPERATIONAL_SEND_RESULT;
    }
    const failure = Object.freeze({ class: "operational" as const, cause });
    if (this.#dtlsObservationPending) {
      this.#pendingSoftFailure ??= failure;
      return;
    }
    this.#emitFailure(failure);
  }

  #failLocal(cause: W10PageLocalCause): void {
    if (this.#closed || this.#softFailurePending) return;
    this.#terminalSendResult = localFailedSendResult(cause);
    const failure = Object.freeze({ class: "local" as const, cause });
    if (this.#dtlsObservationPending) {
      this.#pendingSoftFailure ??= failure;
      return;
    }
    this.#emitFailure(failure);
  }

  #failPolicy(cause: "RATE_LIMITED"): void {
    if (this.#closed || this.#softFailurePending) return;
    this.#emitFailure(Object.freeze({ class: "policy", cause }));
  }

  #requiredChannelClosed(): void {
    this.#failLocal("channel-closed");
  }

  #failHard(cause: W10PageHardCause): void {
    if (this.#closed) return;
    this.#pendingSoftFailure = undefined;
    this.#terminalSendResult = HARD_PROTOCOL_SEND_RESULT;
    this.#emitFailure(Object.freeze({ class: "hard", cause }));
  }

  #finishPendingSoftFailure(): void {
    const failure = this.#pendingSoftFailure;
    if (failure === undefined || this.#closed) return;
    this.#pendingSoftFailure = undefined;
    this.#emitFailure(failure);
  }

  #emitFailure(failure: W10PageCarrierFailure): void {
    if (this.#closed) return;
    if (failure.class === "hard") {
      this.#options.onFailure(failure);
      this.close();
      return;
    }
    if (this.#softFailurePending) return;
    this.#softFailurePending = true;
    this.#clearOpenDeadline();
    this.#clearIceDisconnectDeadline();
    for (const lane of Object.values(this.#lanes)) {
      this.#clearPartial(lane);
      this.#clearSendProgressDeadline(lane);
    }
    this.#emitLogicalState();
    this.#options.onFailure(failure);
  }
}

function laneForChannel(
  channel: Pick<RTCDataChannel, "label">,
): W10DataChannelLane | undefined {
  if (channel.label === W10_DATA_CHANNEL_V1.controlLabel) return "control";
  if (channel.label === W10_DATA_CHANNEL_V1.whiteboardLabel)
    return "whiteboard";
  return undefined;
}

function isConformingMemberChannel(
  channel: Pick<
    RTCDataChannel,
    | "label"
    | "protocol"
    | "ordered"
    | "negotiated"
    | "maxPacketLifeTime"
    | "maxRetransmits"
  >,
  lane: W10DataChannelLane,
): boolean {
  return (
    channel.label ===
      (lane === "control"
        ? W10_DATA_CHANNEL_V1.controlLabel
        : W10_DATA_CHANNEL_V1.whiteboardLabel) &&
    channel.protocol === W10_DATA_CHANNEL_V1.protocol &&
    channel.ordered === true &&
    channel.negotiated === false &&
    channel.maxPacketLifeTime === null &&
    channel.maxRetransmits === null
  );
}

function isDataOnlySdp(sdp: string | undefined): sdp is string {
  if (typeof sdp !== "string" || sdp.length === 0) return false;
  const media = sdp.split(/\r?\n/u).filter((line) => line.startsWith("m="));
  return (
    media.length === 1 &&
    media[0]?.startsWith("m=application ") === true &&
    !/(?:^|\r?\n)m=(?:audio|video)\s/iu.test(sdp)
  );
}

class DtlsObservationUnavailableError extends Error {}
class DtlsCertificateMismatchError extends Error {}
class DtlsTransportFailedError extends Error {}

interface W10TransportStatsRecord extends Record<string, unknown> {
  readonly id: string;
  readonly type: "transport";
  readonly dtlsState: "connected";
  readonly localCertificateId: string;
  readonly remoteCertificateId: string;
}

async function readEstablishedDtlsBinding(
  report: RTCStatsReport,
  subtle: Pick<SubtleCrypto, "digest">,
): Promise<W10EstablishedDtlsBinding> {
  const records: Array<Record<string, unknown>> = [];
  report.forEach((value) => {
    if (isPlainRecord(value)) records.push(value);
  });
  if (
    records.some(
      (value) => value.type === "transport" && value.dtlsState === "failed",
    )
  ) {
    throw new DtlsTransportFailedError();
  }
  const transports = records.filter(
    (value): value is W10TransportStatsRecord =>
      value.type === "transport" && value.dtlsState === "connected",
  );
  if (transports.length !== 1) throw new DtlsObservationUnavailableError();
  const transport = transports[0];
  if (
    transport === undefined ||
    typeof transport.localCertificateId !== "string" ||
    transport.localCertificateId.length === 0 ||
    typeof transport.remoteCertificateId !== "string" ||
    transport.remoteCertificateId.length === 0
  ) {
    throw new DtlsObservationUnavailableError();
  }
  const local = records.filter(
    (value) => value.id === transport.localCertificateId,
  );
  const remote = records.filter(
    (value) => value.id === transport.remoteCertificateId,
  );
  if (local.length !== 1 || remote.length !== 1)
    throw new DtlsObservationUnavailableError();
  const clientFingerprintSha256 = await readCertificateFingerprint(
    local[0],
    subtle,
  );
  const boardFingerprintSha256 = await readCertificateFingerprint(
    remote[0],
    subtle,
  );
  return Object.freeze({
    kind: "webrtc-dtls-v1",
    clientFingerprintSha256,
    boardFingerprintSha256,
  });
}

async function readCertificateFingerprint(
  value: Record<string, unknown> | undefined,
  subtle: Pick<SubtleCrypto, "digest">,
): Promise<string> {
  if (
    value?.type !== "certificate" ||
    typeof value.base64Certificate !== "string" ||
    value.base64Certificate.length === 0 ||
    typeof value.fingerprintAlgorithm !== "string" ||
    value.fingerprintAlgorithm.length === 0 ||
    typeof value.fingerprint !== "string" ||
    value.fingerprint.length === 0
  ) {
    throw new DtlsObservationUnavailableError();
  }
  if (value.fingerprintAlgorithm.toLowerCase() !== "sha-256") {
    throw new DtlsCertificateMismatchError();
  }
  let der: Uint8Array;
  let reported: Uint8Array;
  try {
    der = decodeStrictBase64(value.base64Certificate);
    reported = decodeFingerprint(value.fingerprint);
  } catch {
    throw new DtlsCertificateMismatchError();
  }
  if (der.byteLength === 0) throw new DtlsObservationUnavailableError();
  if (!isCompleteDerSequence(der)) throw new DtlsCertificateMismatchError();
  let digest: Uint8Array;
  try {
    digest = new Uint8Array(
      await subtle.digest("SHA-256", exactArrayBuffer(der)),
    );
  } catch {
    throw new DtlsObservationUnavailableError();
  }
  if (digest.byteLength !== 32 || !equalByteArrays(digest, reported)) {
    throw new DtlsCertificateMismatchError();
  }
  return encodeBase64Url(digest);
}

function decodeStrictBase64(value: string): Uint8Array {
  if (
    !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}(?:==)?|[A-Za-z0-9+/]{3}=?|)$/u.test(
      value,
    ) ||
    value.length % 4 === 1
  ) {
    throw new TypeError("certificate is not strict base64");
  }
  const padded = value.padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = globalThis.atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function decodeFingerprint(value: string): Uint8Array {
  if (!/^[0-9A-Fa-f]{2}(?::[0-9A-Fa-f]{2}){31}$/u.test(value)) {
    throw new TypeError("certificate fingerprint is malformed");
  }
  return Uint8Array.from(value.split(":"), (octet) =>
    Number.parseInt(octet, 16),
  );
}

function isCompleteDerSequence(bytes: Uint8Array): boolean {
  if (bytes.byteLength < 2 || bytes[0] !== 0x30) return false;
  const firstLength = bytes[1];
  if (firstLength === undefined) return false;
  if ((firstLength & 0x80) === 0) return firstLength + 2 === bytes.byteLength;
  const width = firstLength & 0x7f;
  if (width === 0 || width > 4 || bytes.byteLength < 2 + width) return false;
  if (bytes[2] === 0) return false;
  let length = 0;
  for (let index = 0; index < width; index += 1) {
    length = length * 256 + (bytes[2 + index] ?? 0);
  }
  return length >= 128 && 2 + width + length === bytes.byteLength;
}

function exactArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function equalByteArrays(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) return false;
  let difference = 0;
  for (let index = 0; index < left.byteLength; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }
  return difference === 0;
}

function encodeBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return globalThis
    .btoa(binary)
    .replace(/\+/gu, "-")
    .replace(/\//gu, "_")
    .replace(/=+$/u, "");
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isKebabCaseType(value: unknown): value is string {
  return (
    typeof value === "string" && /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/u.test(value)
  );
}

const RETAINED_CLIENT_CONTROL_TYPES: ReadonlySet<string> = new Set([
  "session-statement-request",
  "session-confirmation-report",
  "join",
  "leave",
  "ping",
  "sync-request",
  "peer-update",
  "share-request",
  "share-ready",
  "share-stop",
  "leg-status",
  "rtc-signal",
  "stats-report",
  "error",
]);

const RETAINED_SERVER_CONTROL_TYPES: ReadonlySet<string> = new Set([
  "session-statement-selector",
  "session-statement",
  "session-confirmation-result",
  "join-ack",
  "kick",
  "pong",
  "room-state",
  "peer-joined",
  "peer-left",
  "peer-updated",
  "share-decision",
  "share-revoke",
  "share-changed",
  "host-media",
  "leg-create",
  "leg-close",
  "transport-changed",
  "rtc-signal",
  "error",
]);

function isRetainedClientControlType(type: string): boolean {
  return RETAINED_CLIENT_CONTROL_TYPES.has(type);
}

function isRetainedServerControlType(type: string): boolean {
  return RETAINED_SERVER_CONTROL_TYPES.has(type);
}

function isW10ExactControlType(
  value: unknown,
): value is W10ExactControlRecord["type"] {
  return (
    value === "join-ack" ||
    value === "member-ready" ||
    value === "member-ready-ack" ||
    value === "whiteboard-awareness-rejected" ||
    value === "whiteboard-resync-needed" ||
    value === "whiteboard-resync-request" ||
    value === "whiteboard-resync-start" ||
    value === "whiteboard-resync-start-ack" ||
    value === "whiteboard-resync-ready" ||
    value === "whiteboard-resync-ready-ack" ||
    value === "whiteboard-resync-complete"
  );
}

function isW10SyncWorkType(type: string): boolean {
  return type === "sync-request" || type === "whiteboard-resync-needed";
}

function rateExemptionKey(
  type: string,
  payload: Readonly<Record<string, unknown>>,
): string | undefined {
  if (
    type !== "whiteboard-resync-start" &&
    type !== "whiteboard-resync-ready-ack"
  ) {
    return undefined;
  }
  if (
    typeof payload.sessionId !== "string" ||
    typeof payload.memberLegId !== "string" ||
    !Number.isSafeInteger(payload.memberEpoch) ||
    !Number.isSafeInteger(payload.resyncId)
  ) {
    return undefined;
  }
  return `${type}\u0000${payload.sessionId}\u0000${payload.memberLegId}\u0000${String(payload.memberEpoch)}\u0000${String(payload.resyncId)}`;
}

function isExactRateLimitedPayload(
  payload: Readonly<Record<string, unknown>>,
): boolean {
  const keys = Object.keys(payload);
  return (
    keys.length === 3 &&
    keys[0] === "code" &&
    keys[1] === "message" &&
    keys[2] === "fatal" &&
    payload.code === "RATE_LIMITED" &&
    typeof payload.message === "string" &&
    payload.fatal === false
  );
}

const ACCEPTED_SEND_RESULT: W10PageSendResult = Object.freeze({
  status: "accepted",
});
const PAUSED_SEND_RESULT: W10PageSendResult = Object.freeze({
  status: "paused",
});
const OPERATIONAL_SEND_RESULT: W10PageSendResult = Object.freeze({
  status: "operational-failed",
  reason: "WHITEBOARD_MESSAGE_TOO_LARGE",
});
const HARD_PROTOCOL_SEND_RESULT: W10PageSendResult = Object.freeze({
  status: "hard-protocol-failed",
  reason: "Y_PROTOCOL_INTEGRITY",
});

function localFailedSendResult(cause: W10PageLocalCause): W10PageSendResult {
  return Object.freeze({ status: "local-failed", cause });
}
interface Deferred<T> {
  promise: Promise<T>;
  resolve(value: T): void;
  reject(reason: unknown): void;
}

function deferred<T>(): Deferred<T> {
  let resolvePromise!: (value: T) => void;
  let rejectPromise!: (reason: unknown) => void;
  const promise = new Promise<T>((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
  return { promise, resolve: resolvePromise, reject: rejectPromise };
}

function stopTracks(stream: MediaStream): void {
  disposeCapture(stream);
}

function syntheticKey(hostId: string): string {
  return `klass-share.e2e-synthetic.v1.${hostId}`;
}

/** Regression seam: capture cleanup must happen before the persisted grant is restored. */
export function restoreGrantAfterCaptureCleanup(
  grantId: string,
  installCapture: () => unknown,
): string {
  installCapture();
  return grantId;
}

export function shouldPreserveRtcDuringReconnect(
  signaling: SignalingSnapshot,
  localCaptureActive: boolean,
): boolean {
  const share = signaling.room?.share;
  return (
    localCaptureActive &&
    share?.status === "active" &&
    share.sourceClientId === signaling.clientId
  );
}

export function shouldTearDownClientResources(
  signaling: SignalingSnapshot,
): boolean {
  return (
    signaling.phase === "terminal-error" ||
    signaling.phase === "manual-reconnect" ||
    signaling.phase === "room-closed" ||
    signaling.phase === "stopped" ||
    signaling.lastError?.fatal === true
  );
}

export function shouldShowDisplayShareRequest(state: ClientViewState): boolean {
  return (
    state.signaling.capabilities.canCaptureScreen &&
    !state.localCaptureActive &&
    !state.captureRequestPending
  );
}

function shareDenialMessage(reason: string): string {
  const messages: Record<string, string> = {
    denied: strings.errors.shareRequest.denied,
    busy: strings.errors.shareRequest.busy,
    "unsupported-device": strings.errors.shareRequest.unsupportedDevice,
    "already-sharing": strings.errors.shareRequest.alreadySharing,
  };
  return messages[reason] ?? strings.errors.shareRequest.fallback;
}
